import React, { useEffect, useMemo, useRef, useState } from 'react';
import './AuctionMarketTab.css';
import {
  Search,
  PlusCircle,
  Loader2,
  ExternalLink,
  PencilLine,
  Trash2,
  CheckCircle2,
  X,
  Tag,
  Fish,
  Target,
  Bandage,
  Plane,
  PiggyBank,
  KeyRound,
  Dices,
  ChevronDown,
  Flag,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import TeamBadge from '../TeamBadge';
import TeamNextFixtures from '../TeamNextFixtures';
import Tooltip from '../Tooltip';
import { buildTeamEconomy } from './auctionRosterUtils';
import {
  ROLE_ORDER,
  ROLE_LABELS,
  RoleChip,
  AuctionTeamAvatar,
  SORT_OPTIONS,
  v,
  fmt,
  getSeasonBlendWeights,
  getPlayProbabilitySortValue,
} from './auctionCommon';

const HIDDEN_TEAM_NAME = '__ALTRO__';
const MANTRA_ROLE_FILTERS = ['all', 'P', 'Dd', 'Dc', 'Ds', 'B', 'E', 'M', 'C', 'W', 'T', 'A', 'Pc'];
const CATEGORY_FILTERS = ['all', 'none', 'trap', 'target', 'injury', 'internationalcup', 'cheap', 'key', 'wager', 'goodstarter', 'custom'];

const DEFAULT_CUSTOM_LABEL = 'Personalizzata';

const CATEGORY_META = {
  all: { label: 'Tutte', description: 'Mostra tutti i giocatori', icon: Tag },
  none: { label: 'Nessuna', description: 'Nessuna categoria assegnata', icon: Tag },
  trap: { label: 'Esca', description: 'Giocatore da usare come esca in asta', icon: Fish },
  target: { label: 'Obiettivo', description: 'Giocatore che ti interessa davvero', icon: Target },
  injury: { label: 'Infortunio', description: 'Giocatore soggetto a problemi fisici', icon: Bandage },
  internationalcup: {
    label: 'Coppa Internaz.',
    description: 'Giocatore potenzialmente convocabile per competizioni internazionali (es. Coppa Africa)',
    icon: Plane,
  },
  cheap: { label: 'Economico', description: 'Giocatore con un costo più basso del suo valore reale', icon: PiggyBank },
  key: { label: 'Chiave', description: 'Giocatore che deve essere tuo', icon: KeyRound },
  wager: { label: 'Scommessa', description: 'Giocatore che potrebbe over-performare', icon: Dices },
  goodstarter: { label: 'Buon titolare', description: 'Titolare fisso nella sua squadra', icon: ShieldCheck },
  custom: { label: DEFAULT_CUSTOM_LABEL, description: 'Etichetta personalizzata scelta dall’utente', icon: Sparkles },
};

// Fascia assegnata manualmente dall'operatore negli Svincolati (1 = top .. 5 = più economica)
const TIER_FILTERS = ['all', 'none', '1', '2', '3', '4', '5'];

function getPlayerTier(player, playerTiersById = {}) {
  const key = String(player?.id || '');
  const n = Number(playerTiersById?.[key] || 0);
  return n >= 1 && n <= 5 ? n : 0;
}

const ROLE_FULL_LABELS = {
  P: 'Portiere',
  D: 'Difensore',
  Dd: 'Difensore destro',
  Ds: 'Difensore sinistro',
  Dc: 'Difensore centrale',
  B: 'Braccetto',
  M: 'Mediano',
  E: 'Esterno',
  C: 'Centrocampista',
  W: 'Ala',
  T: 'Trequartista',
  A: 'Attaccante',
  Pc: 'Punta centrale',
};

const CLASSIC_ROLE_LABELS = {
  all: 'Tutti',
  P: 'Portieri',
  D: 'Difensori',
  C: 'Centrocampisti',
  A: 'Attaccanti',
};

const TEAM_BONUS_COEFF = {
  inter: 1.4,
  milan: 1.14,
  juventus: 1.13,
  napoli: 1.12,
  roma: 1.1,
  atalanta: 1.1,
  bologna: 1.06,
  lazio: 1.05,
  fiorentina: 1.05,
  torino: 1.03,
  como: 1.1,
  else: 1.0,
};

const FILTER_STORAGE_PREFIX = 'fantapp-auction-market-filters:v2';

// Sort modes che NON sono già visibili nelle chip della card.
// Quando attivi, mostrano un badge evidenziato con il valore del criterio.
const SORT_BADGE = {
  caps_desc:      { label: 'Pres.',  getValue: (p)           => fmt(v(p.caps))    },
  goals_desc:     { label: 'Gol',    getValue: (p)           => fmt(v(p.goals))   },
  assists_desc:   { label: 'Ass.',   getValue: (p)           => fmt(v(p.assists)) },
  pma_delta_desc: { label: 'ΔPMA',   getValue: (p, pmaEntry, suggestedBid) => {
    const avg = pmaEntry?.average;
    if (avg === null || avg === undefined || suggestedBid === undefined) return '—';
    const delta = v(suggestedBid) - v(avg);
    return (delta >= 0 ? '+' : '') + fmt(delta);
  }},
  playprob_desc: { label: 'Prob.',   getValue: (p, pmaEntry, suggestedBid, playProb) => {
    const prob = Number(playProb?.prob);
    if (!playProb || !Number.isFinite(prob)) return '—';
    return `${playProb.status === 'starter' ? 'Tit.' : 'Panc.'} ${prob}%`;
  }},
};

function normalizeText(value = '') {
  return String(value)
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function splitRoleTokens(value = '') {
  return String(value || '')
    .split(/[\/,|+]/g)
    .flatMap((part) => part.split(/\s+/g))
    .map((part) => part.trim())
    .filter(Boolean);
}

function normalizeMantraToken(token = '') {
  const cleaned = String(token || '').trim();
  if (!cleaned) return '';

  const lower = cleaned.toLowerCase();
  if (lower === 'pc') return 'Pc';
  if (lower === 'dd') return 'Dd';
  if (lower === 'dc') return 'Dc';
  if (lower === 'ds') return 'Ds';
  if (lower === 'w') return 'W';
  if (lower === 't') return 'T';
  if (lower === 'm') return 'M';
  if (lower === 'e') return 'E';
  if (lower === 'c') return 'C';
  if (lower === 'a') return 'A';
  if (lower === 'b') return 'B';
  if (lower === 'p') return 'P';

  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
}

function getRoleTokens(playerRole, rolesMode) {
  if (rolesMode !== 'mantra') {
    const role = String(playerRole || '').trim().toUpperCase();
    return role ? [role] : [];
  }

  if (Array.isArray(playerRole)) {
    return playerRole.map((r) => normalizeMantraToken(r)).filter(Boolean);
  }

  return splitRoleTokens(playerRole).map(normalizeMantraToken).filter(Boolean);
}

function getPrimaryRoleValue(playerRole, rolesMode) {
  const tokens = getRoleTokens(playerRole, rolesMode);
  return tokens[0] || (rolesMode === 'mantra' ? '—' : 'A');
}

function getMantraRoleGroup(roleValue = '') {
  const token = normalizeMantraToken(roleValue);
  const upper = token.toUpperCase();

  if (upper === 'P') return 'P';
  if (upper === 'D') return 'D';
  if (['DD', 'DC', 'DS', 'B'].includes(upper)) return 'D';
  if (['E', 'M', 'C', 'W', 'T'].includes(upper)) return 'C';
  if (['A', 'PC'].includes(upper)) return 'A';
  return 'A';
}

function getRoleFullLabel(role) {
  return ROLE_FULL_LABELS[String(role || '').trim()] || String(role || '—').trim() || '—';
}

function getOverlayRoleLabel(role, rolesMode) {
  if (rolesMode === 'mantra') {
    if (role === 'P') return 'Portieri';
    if (role === 'M') return 'Giocatori di movimento';
  }

  return ROLE_LABELS[role] || role;
}

function normalizePlayerCategory(value = 'none') {
  const normalized = String(value || 'none').trim().toLowerCase();
  return CATEGORY_FILTERS.includes(normalized) ? normalized : 'none';
}

function getCategoryMeta(category = 'none', customLabel) {
  const normalized = normalizePlayerCategory(category);
  const meta = CATEGORY_META[normalized] || CATEGORY_META.none;
  if (normalized === 'custom' && customLabel && customLabel.trim()) {
    return { ...meta, label: customLabel.trim() };
  }
  return meta;
}

function getPlayerCategory(player, playerCategoriesById = {}) {
  const key = String(player?.id || '');
  const fromMap = playerCategoriesById?.[key];
  const fromPlayer =
    player?.category ||
    player?.categoryName ||
    player?.playerCategory ||
    player?.playerSnapshot?.category ||
    player?.playerSnapshot?.categoryName ||
    player?.playerSnapshot?.playerCategory ||
    'none';

  return normalizePlayerCategory(fromMap ?? fromPlayer);
}

function getCategoryLabel(category, customLabel) {
  return getCategoryMeta(category, customLabel).label;
}

function getTeamBonusCoeff(teamName = '') {
  const key = normalizeText(teamName).replace(/[^a-z]/g, '');
  return TEAM_BONUS_COEFF[key] ?? TEAM_BONUS_COEFF.else ?? 1;
}

function loadStoredFilters(storageKey) {
  if (typeof window === 'undefined' || !storageKey) return null;

  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function saveStoredFilters(storageKey, filters) {
  if (typeof window === 'undefined' || !storageKey) return;

  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(filters));
  } catch {
    // ignore
  }
}

function CategoryChip({ category, className = '', title, customLabel }) {
  const meta = getCategoryMeta(category, customLabel);
  const Icon = meta.icon;
  const normalized = normalizePlayerCategory(category);

  return (
    <Tooltip
      as="span"
      className={`market-category-chip market-category-chip--${normalized} ${className}`.trim()}
      content={title || meta.description}
      aria-label={title || meta.description}
    >
      <span className="market-category-chip__icon" aria-hidden="true">
        {Icon ? <Icon size={13} /> : null}
      </span>
      <span className="market-category-chip__label">{meta.label}</span>
    </Tooltip>
  );
}

function renderRoleBadges(roleValue, rolesMode) {
  if (rolesMode !== 'mantra') {
    return <RoleChip role={String(roleValue || 'A').trim().toUpperCase() || 'A'} />;
  }

  const tokens = getRoleTokens(roleValue, 'mantra');

  if (!tokens.length) {
    return (
      <span className="auction-role-chip auction-role-chip--mantra">
        —
      </span>
    );
  }

  return (
    <span className="auction-role-badges" aria-label={tokens.join(' / ')}>
      {tokens.map((token, idx) => {
        const group = getMantraRoleGroup(token);
        return (
          <span
            key={`${token}-${group}-${idx}`}
            className={`auction-role-chip auction-role-chip--mantra auction-role-chip--mantra-${group}`}
            aria-label={token}
          >
            {token}
          </span>
        );
      })}
    </span>
  );
}

function renderFullRoleBadges(roleValue, rolesMode) {
  const tokens = getRoleTokens(roleValue, rolesMode);

  if (!tokens.length) {
    return <span className="selected-role-pill selected-role-pill--empty">—</span>;
  }

  return (
    <span className="selected-role-badges" aria-label={tokens.map(getRoleFullLabel).join(' / ')}>
      {tokens.map((token, idx) => {
        const group = getMantraRoleGroup(token);
        return (
          <span
            key={`${token}-${group}-${idx}`}
            className={`selected-role-pill selected-role-pill--${group}`}
          >
            {getRoleFullLabel(token)}
          </span>
        );
      })}
    </span>
  );
}

// Multi-selezione ruolo: nessun ruolo selezionato ("Tutti") = nessun filtro;
// altrimenti il giocatore passa se corrisponde ad ALMENO uno dei ruoli scelti
// (utile in Mantra per vedere insieme più ruoli, es. "Dc" + "Ds").
function playerMatchesRoles(player, filterRoles, rolesMode) {
  if (!filterRoles?.length) return true;
  const tokens = getRoleTokens(player?.role, rolesMode);
  return filterRoles.some((role) => tokens.includes(role));
}

function toDateSafe(ts) {
  if (!ts) return null;

  if (ts instanceof Date) {
    return Number.isNaN(ts.getTime()) ? null : ts;
  }

  if (typeof ts === 'number') {
    const ms = ts < 1e12 ? ts * 1000 : ts;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof ts === 'string') {
    const parsed = Date.parse(ts);
    if (Number.isNaN(parsed)) return null;
    const d = new Date(parsed);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof ts === 'object') {
    try {
      if (typeof ts.toDate === 'function') {
        const d = ts.toDate();
        if (d instanceof Date && !Number.isNaN(d.getTime())) return d;
      }
    } catch {
      // ignore
    }

    if (typeof ts.seconds === 'number') {
      const d = new Date(ts.seconds * 1000 + Math.floor((ts.nanoseconds || 0) / 1e6));
      return Number.isNaN(d.getTime()) ? null : d;
    }

    if (typeof ts._seconds === 'number') {
      const d = new Date(ts._seconds * 1000 + Math.floor((ts._nanoseconds || 0) / 1e6));
      return Number.isNaN(d.getTime()) ? null : d;
    }

    if (typeof ts._milliseconds === 'number') {
      const d = new Date(ts._milliseconds);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }

  return null;
}

function formatDate(ts) {
  const d = toDateSafe(ts);
  if (!d) return '—';

  return d.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

// Quanti giocatori DEVONO ancora essere acquistati (per qualunque ruolo) per
// raggiungere il minimo di rosa — non quanti se ne POTREBBERO ancora
// acquistare fino al massimo (vedi remainingByRole per la capienza).
function sumRemainingMinSlots(stats) {
  return ROLE_ORDER.reduce((acc, role) => acc + v(stats?.remainingMinByRole?.[role] || 0), 0);
}

function clampNumber(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function smoothstep(min, max, value) {
  if (max <= min) return 0;
  const x = clampNumber((value - min) / (max - min), 0, 1);
  return x * x * (3 - 2 * x);
}

function smartRoundCredits(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return 1;

  if (n <= 10) return Math.max(1, Math.round(n));
  if (n <= 30) return Math.max(1, Math.round(n / 2) * 2);
  if (n <= 80) return Math.max(1, Math.round(n / 5) * 5);
  if (n <= 150) return Math.max(1, Math.round(n / 10) * 10);

  return Math.max(1, Math.round(n / 10) * 10);
}

function getSeasonValue(source) {
  return source?.value ?? source?.pma ?? source?.pmaCurrent ?? source?.averagePma ?? null;
}

function getGoalsForFromSource(source, roleFamily) {
  if (roleFamily === 'P') return null;
  return source?.goals ?? null;
}

function getGoalsAgainstFromSource(source, roleFamily) {
  if (roleFamily !== 'P') return null;
  return source?.gotgoals ?? source?.goalsConceded ?? source?.goals_against ?? null;
}

function getAssistsFromSource(source, roleFamily) {
  if (roleFamily === 'P') return null;
  return source?.assists ?? null;
}

// Versione abbreviata di getPlayProbabilityLabel (auctionCommon.js), solo
// per la StatTile "Impiego" nel pannello "Giocatore selezionato": qui lo
// spazio è stretto (condivide la riga con "Riserva"), le altre viste che
// usano getPlayProbabilityLabel (Formazione, Svincolati, Simula rosa) hanno
// più respiro e restano con l'etichetta estesa.
function getCompactPlayProbabilityLabel(entry) {
  const prob = Number(entry?.prob);
  if (!entry || !Number.isFinite(prob)) return '—';
  return entry.status === 'starter' ? `Tit (${prob}%)` : `Panch (${prob}%)`;
}

function getSavesFromSource(source, roleFamily) {
  if (roleFamily !== 'P') return null;
  return source?.savedPenalties ?? source?.penaltiesSaved ?? source?.penalties_saved ?? source?.saves ?? null;
}

function getRoleFamily(roleValue, rolesMode) {
  if (rolesMode === 'mantra') {
    const tokens = getRoleTokens(roleValue, 'mantra').map((t) => String(t || '').toUpperCase());

    if (tokens.includes('P')) return 'P';
    if (tokens.some((t) => ['DD', 'DC', 'DS', 'B'].includes(t))) return 'D';
    if (tokens.some((t) => ['E', 'M', 'C', 'W', 'T'].includes(t))) return 'C';
    if (tokens.some((t) => ['A', 'PC'].includes(t))) return 'A';
    return 'C';
  }

  const role = String(roleValue || '').trim().toUpperCase();
  if (role === 'P') return 'P';
  if (role === 'D') return 'D';
  if (role === 'C') return 'C';
  return 'A';
}

// Max bid reale: quanto la squadra può offrire in astratto sulla PROSSIMA
// puntata, senza un giocatore specifico selezionato. Si tiene da parte 1
// credito per ogni giocatore ancora obbligatorio da acquistare per il
// minimo di rosa (non fino al massimo, che resta libero), tranne uno: dato
// che si può sempre scegliere di spendere questa puntata su UN ruolo che
// deve ancora raggiungere il minimo (finché ne resta almeno uno), il caso
// migliore è che questa puntata stessa copra uno degli obblighi residui.
function getTeamMaxBidReal(stats, rosterLimits, rolesMode) {
  const creditsRemaining = v(stats?.remaining || 0);

  const remainingMinSlots =
    rolesMode === 'classic'
      ? sumRemainingMinSlots(stats)
      : Number(stats?.remainingMinByRole?.P ?? 0) + Number(stats?.remainingMinByRole?.M ?? 0);

  return Math.max(creditsRemaining - Math.max(remainingMinSlots - 1, 0), 0);
}

// Prezzo "reattivo": a differenza del prezzo suggerito (ancorato al budget
// INIZIALE dell'asta e alla qualità del giocatore), stima quanto vale il
// giocatore selezionato guardando i crediti REALMENTE residui, in questo
// momento, di tutte le squadre ancora attive su quel ruolo. Confronta il
// budget medio "per slot ancora da riempire" attuale con quello che ci
// sarebbe stato a inizio asta (stessa proporzione, budget pieno / rosa
// piena): se il mercato per quel ruolo si è prosciugato (tutti arrivano con
// pochi crediti residui rispetto agli slot rimasti) il prezzo reattivo
// scende sotto il prezzo suggerito; se il mercato è ancora ricco resta
// vicino o sopra. Sempre limitato a quanto la squadra più "ricca" rimasta su
// quel ruolo può realisticamente permettersi.
function getReactivePrice({ player, auction, rolesMode, teamEconomy, teamNames, rosterLimits, suggestedBid }) {
  if (!player || !suggestedBid) {
    return { reactiveBid: 0, liquidityRatio: 1, sampleSize: 0 };
  }

  const slotKey = getPurchaseRoleSlot(player.role, rolesMode);
  const initialCredits = Math.max(v(auction?.credits || 0), 1);

  const totalRosterSlots =
    rolesMode === 'classic'
      ? ROLE_ORDER.reduce((acc, role) => acc + Number(rosterLimits?.[role] || 0), 0)
      : Number(rosterLimits?.P || 0) + Number(auction?.mantraMovementLimit ?? rosterLimits?.M ?? 0);

  const initialPerSlotBudget = initialCredits / Math.max(totalRosterSlots, 1);

  const perSlotBudgets = [];
  const maxBidsForRole = [];

  (teamNames || []).forEach((team) => {
    const stats = teamEconomy?.[team];
    if (!stats) return;

    // Squadra già senza slot residui per questo ruolo: fuori mercato, i suoi
    // crediti non contano per stimare quanto vale ORA un giocatore di questo ruolo.
    const roleSlotsLeft = Number(stats?.remainingByRole?.[slotKey] || 0);
    if (roleSlotsLeft <= 0) return;

    const totalSlotsLeft =
      rolesMode === 'classic'
        ? sumRemainingMinSlots(stats)
        : Number(stats?.remainingMinByRole?.P || 0) + Number(stats?.remainingMinByRole?.M || 0);

    perSlotBudgets.push(Number(stats?.remaining || 0) / Math.max(totalSlotsLeft, 1));
    maxBidsForRole.push(getTeamMaxBidReal(stats, rosterLimits, rolesMode));
  });

  if (!perSlotBudgets.length) {
    return { reactiveBid: suggestedBid, liquidityRatio: 1, sampleSize: 0 };
  }

  const avgPerSlotBudget = perSlotBudgets.reduce((a, b) => a + b, 0) / perSlotBudgets.length;
  const liquidityRatio = clampNumber(avgPerSlotBudget / initialPerSlotBudget, 0.15, 2.5);
  const marketCeiling = Math.max(...maxBidsForRole, 1);

  const reactiveBid = smartRoundCredits(
    Math.max(1, Math.min(suggestedBid * liquidityRatio, marketCeiling))
  );

  return { reactiveBid, liquidityRatio, sampleSize: perSlotBudgets.length, marketCeiling };
}

function getPerformanceCoreScore(source, roleFamily) {
  if (!source) return 0;

  const caps = v(source.caps);
  const markavg = v(source.markavg);
  const fmarkavg = v(source.fmarkavg);
  const goals = v(getGoalsForFromSource(source, roleFamily));
  const goalsAgainst = v(getGoalsAgainstFromSource(source, roleFamily));
  const assists = v(getAssistsFromSource(source, roleFamily));
  const saves = v(getSavesFromSource(source, roleFamily));
  const yc = v(source.ycards);
  const rc = v(source.rcards);
  const owngoals = v(source.owngoals);
  const penalties = v(source.penalties);
  const spenalties = v(source.spenalties);
  const appearanceScore = clampNumber((Math.log1p(caps) / Math.log1p(38)) * 18.5, 0, 18.5);
  const availabilityRatio = clampNumber(caps / 20, 0, 1);
  const availabilityMultiplier = 0.35 + (availabilityRatio * 0.65);
  const ratingScore = clampNumber(((markavg - 5.5) * 2.5) + ((fmarkavg - 5.6) * 2.0), -5, 15);

  let productionScore = 0;

  if (roleFamily === 'P') {
    productionScore = clampNumber((saves * 4.6) - (goalsAgainst * 0.5), -6, 18);
  } else if (roleFamily === 'D') {
    productionScore = clampNumber((goals * 5.3) + (assists * 3.1), 0, 18);
  } else if (roleFamily === 'C') {
    productionScore = clampNumber((goals * 4.1) + (assists * 4.0), 0, 22);
  } else {
    productionScore = clampNumber((goals * 5.8) + (assists * 4.5), 0, 28);
  }

  const disciplinePenalty = clampNumber(
    yc * 0.28 + rc * 1.35 + owngoals * 1.15 + penalties * 0.06 + spenalties * 0.03,
    0,
    14
  );

  return Math.max(0, (appearanceScore + ratingScore + productionScore - disciplinePenalty) * availabilityMultiplier);
}

function getMarketStrengthScore(player, rolesMode) {
  if (!player) return 0;

  const roleFamily = getRoleFamily(player?.role, rolesMode);
  const current = player || {};
  const prev = player?.previousSeasonStats || null;

  const teamCoeff = getTeamBonusCoeff(current?.team || current?.playerTeam || current?.playerSnapshot?.team || '');
  const currentValue = Math.max(v(getSeasonValue(current)), 0);
  const previousValue = Math.max(v(getSeasonValue(prev)), 0);

  const currentScore = getPerformanceCoreScore(current, roleFamily);
  const previousScore = prev ? getPerformanceCoreScore(prev, roleFamily) * 0.78 : 0;

  // Pesi fissi 0.62/0.25: a inizio stagione (0 presenze correnti) currentScore
  // è quasi sempre 0 e quel peso andava sprecato. Lo spostiamo sullo storico
  // finché non abbiamo abbastanza presenze reali di quest'anno; a stagione
  // piena il risultato è identico a prima.
  const { currentWeight: perfCurrentWeight, previousWeight: perfPreviousWeight } =
    getSeasonBlendWeights(0.62, 0.25, v(current.caps));

  const continuity = clampNumber((v(current.caps) + v(prev?.caps)) * 0.14, 0, 8);
  const valueSignal =
    Math.log1p(Math.max(currentValue, previousValue, 0)) *
    (roleFamily === 'A' ? 4.0 : roleFamily === 'C' ? 3.45 : roleFamily === 'D' ? 3.1 : 2.6);

  const teamSignal = clampNumber((teamCoeff - 1) * 28, 0, 10);

  return (currentScore * perfCurrentWeight) + (previousScore * perfPreviousWeight) + continuity + valueSignal + teamSignal;
}

function getPurchaseRoleSlot(roleValue, rolesMode) {
  if (rolesMode !== 'mantra') {
    return String(getPrimaryRoleValue(roleValue, rolesMode) || 'A').trim();
  }

  return getMantraPurchaseBucket(roleValue);
}

function getSlotLabel(slotKey, rolesMode) {
  if (rolesMode === 'mantra') {
    return slotKey === 'P' ? 'portieri' : 'giocatori di movimento';
  }

  return ROLE_LABELS[slotKey] || slotKey;
}

function buildMarketContext(players, rolesMode) {
  const entries = (players || []).map((player) => {
    const roleFamily = getRoleFamily(player?.role, rolesMode);
    const score = getMarketStrengthScore(player, rolesMode);
    return {
      id: String(player.id),
      roleFamily,
      score,
    };
  });

  const byId = new Map();
  const familyById = new Map();

  const rankedOverall = [...entries].sort((a, b) => b.score - a.score);
  const totalOverall = rankedOverall.length;

  rankedOverall.forEach((entry, index) => {
    const percentile = totalOverall <= 1 ? 1 : 1 - index / Math.max(totalOverall - 1, 1);
    byId.set(entry.id, {
      rank: index + 1,
      total: totalOverall,
      percentile,
      score: entry.score,
      roleFamily: entry.roleFamily,
    });
  });

  const families = ['P', 'D', 'C', 'A'];
  families.forEach((family) => {
    const rankedFamily = entries
      .filter((item) => item.roleFamily === family)
      .sort((a, b) => b.score - a.score);

    const totalFamily = rankedFamily.length;

    rankedFamily.forEach((entry, index) => {
      const percentile = totalFamily <= 1 ? 1 : 1 - index / Math.max(totalFamily - 1, 1);
      familyById.set(`${entry.id}:${family}`, {
        rank: index + 1,
        total: totalFamily,
        percentile,
        score: entry.score,
        roleFamily: family,
      });
    });
  });

  return {
    byId,
    familyById,
    topScore: rankedOverall[0]?.score || 0,
    medianScore: rankedOverall[Math.floor(totalOverall / 2)]?.score || 0,
  };
}

function getSmartSuggestedBid({ player, auction, rolesMode, marketContext }) {
  if (!player) {
    return {
      suggestedBid: 0,
      rawSuggested: 0,
      band: 'consigliato',
      note: '',
      overMax: false,
      deltaOverMax: 0,
      anchorValue: 0,
      currentScore: 0,
      previousScore: 0,
      budgetCeiling: 0,
      budgetHardCap: null,
      roleFamily: 'C',
      marketPercentile: 0.5,
      familyPercentile: 0.5,
      marketRank: 0,
      teamCoeff: 1,
    };
  }

  const roleFamily = getRoleFamily(player?.role, rolesMode);
  const current = player || {};
  const prev = player?.previousSeasonStats || null;

  const teamCoeff = getTeamBonusCoeff(current?.team || current?.playerTeam || current?.playerSnapshot?.team || '');
  const currentValue = Math.max(v(getSeasonValue(current)), 0);
  const previousValue = Math.max(v(getSeasonValue(prev)), 0);

  const fallbackValue = {
    P: 2.5,
    D: 3.5,
    C: 4.5,
    A: 5.5,
  };

  const anchorValue =
    currentValue > 0
      ? currentValue
      : previousValue > 0
        ? previousValue
        : fallbackValue[roleFamily] || 3;

  const currentScore = getPerformanceCoreScore(current, roleFamily);
  const previousScore = prev ? getPerformanceCoreScore(prev, roleFamily) : 0;

  const caps = v(current.caps);
  const prevCaps = v(prev?.caps);
  const experienceScore = clampNumber((caps + prevCaps) * 0.16, 0, 10);

  // Pesi che si spostano sullo storico quando le presenze di quest'anno sono
  // poche o zero (es. a inizio campionato), invece di pesi fissi che a quel
  // punto sprecano gran parte del segnale su un valore vicino allo zero. A
  // stagione piena (>= soglia) il comportamento torna identico a prima.
  const { currentWeight: perfPulseCurrentWeight, previousWeight: perfPulsePreviousWeight } =
    getSeasonBlendWeights(0.62, 0.38, caps);
  const { currentWeight: perfFloorCurrentWeight, previousWeight: perfFloorPreviousWeight } =
    getSeasonBlendWeights(0.82, 0.44, caps);
  const { currentWeight: availCurrentWeight, previousWeight: availPreviousWeight } =
    getSeasonBlendWeights(0.7, 0.3, caps);

  const marketInfo = marketContext?.byId?.get(String(player.id)) || null;
  const familyInfo = marketContext?.familyById?.get(`${String(player.id)}:${roleFamily}`) || null;

  const marketPercentile = Number.isFinite(marketInfo?.percentile) ? marketInfo.percentile : 0.5;
  const familyPercentile = Number.isFinite(familyInfo?.percentile) ? familyInfo.percentile : marketPercentile;
  const marketRank = Number.isFinite(marketInfo?.rank) ? marketInfo.rank : 0;

  const availabilityIndex = clampNumber((caps * availCurrentWeight + prevCaps * availPreviousWeight) / 25, 0, 1);

  const valuePulse = clampNumber(
    Math.log1p(anchorValue) / Math.log1p(120),
    0,
    1
  );
  const eliteValueFactor = clampNumber(
    Math.pow(valuePulse, 1.75),
    0,
    1
  );

  const qualityIndex = clampNumber(
    marketPercentile * 0.28 +
    familyPercentile * 0.18 +
    eliteValueFactor * 0.38 +
    availabilityIndex * 0.12 +
    clampNumber((teamCoeff - 1) * 1.1, 0, 0.04),
    0,
    1
  );

  const performancePulse = clampNumber(
    (currentScore * perfPulseCurrentWeight + previousScore * perfPulsePreviousWeight) / 42,
    0,
    1
  );
  const continuityPulse = clampNumber(experienceScore / 10, 0, 1);

  let tier = 'low';
  if (qualityIndex >= 0.92) tier = 'superstar';
  else if (qualityIndex >= 0.78) tier = 'star';
  else if (qualityIndex >= 0.62) tier = 'premium';
  else if (qualityIndex >= 0.42) tier = 'solid';
  else if (qualityIndex >= 0.22) tier = 'budget';

  const SHARE_TABLE = {
    P: {
      low: [0.003, 0.008],
      budget: [0.008, 0.016],
      solid: [0.016, 0.03],
      premium: [0.03, 0.055],
      star: [0.055, 0.085],
      superstar: [0.085, 0.14],
    },
    D: {
      low: [0.004, 0.01],
      budget: [0.01, 0.02],
      solid: [0.02, 0.04],
      premium: [0.04, 0.07],
      star: [0.07, 0.11],
      superstar: [0.11, 0.18],
    },
    C: {
      low: [0.005, 0.012],
      budget: [0.012, 0.024],
      solid: [0.024, 0.05],
      premium: [0.05, 0.09],
      star: [0.09, 0.15],
      superstar: [0.15, 0.24],
    },
    A: {
      low: [0.006, 0.016],
      budget: [0.016, 0.032],
      solid: [0.032, 0.068],
      premium: [0.068, 0.115],
      star: [0.115, 0.19],
      superstar: [0.19, 0.3],
    },
  };

  const [minShare, maxShare] = SHARE_TABLE[roleFamily]?.[tier] || SHARE_TABLE.C.solid;
  const tierMin =
    tier === 'superstar'
      ? 0.92
      : tier === 'star'
        ? 0.78
        : tier === 'premium'
          ? 0.62
          : tier === 'solid'
            ? 0.42
            : tier === 'budget'
              ? 0.22
              : 0;

  const tierMax =
    tier === 'superstar'
      ? 1
      : tier === 'star'
        ? 0.92
        : tier === 'premium'
          ? 0.78
          : tier === 'solid'
            ? 0.62
            : tier === 'budget'
              ? 0.42
              : 0.22;

  const tierProgress = smoothstep(tierMin, tierMax, qualityIndex);
  let suggestedShare = lerp(minShare, maxShare, tierProgress);

  const roleLift = {
    P: 0.94,
    D: 1.0,
    C: 1.06,
    A: 1.12,
  }[roleFamily] || 1.0;

  const teamLift = clampNumber(
    0.95 + (teamCoeff - 1) * 0.9,
    0.95,
    1.18
  );
  const valueLift =
    1 + (valuePulse - 0.35) * (roleFamily === 'A' ? 0.26 : roleFamily === 'C' ? 0.21 : roleFamily === 'D' ? 0.16 : 0.1);
  const performanceLift =
    1 + (performancePulse - 0.35) * (roleFamily === 'A' ? 0.2 : roleFamily === 'C' ? 0.18 : roleFamily === 'D' ? 0.14 : 0.1);
  const availabilityLift = 0.35 + (availabilityIndex * 0.65);
  const valueLiftStrong =
    0.55 + eliteValueFactor * 0.85;
  const historyLift = prev ? 1 + (continuityPulse - 0.28) * 0.08 : 0.98;

  suggestedShare *=
    roleLift *
    teamLift *
    valueLift *
    valueLiftStrong *
    performanceLift *
    historyLift *
    availabilityLift;
  suggestedShare = clampNumber(suggestedShare, minShare, maxShare);

  const initialBudget = Math.max(v(auction?.credits || 0), 1);
  const budgetBased = initialBudget * suggestedShare;

  const valueFloor =
    anchorValue *
    (roleFamily === 'A' ? 1.38 : roleFamily === 'C' ? 1.2 : roleFamily === 'D' ? 1.04 : 0.92) *
    teamCoeff;

  const performanceFloor =
    (currentScore * perfFloorCurrentWeight + previousScore * perfFloorPreviousWeight + experienceScore * 0.24) *
    (roleFamily === 'A' ? 1.08 : roleFamily === 'C' ? 1.04 : 0.98);

  const marketAnchor =
    marketPercentile >= 0.98
      ? initialBudget * (roleFamily === 'A' ? 0.22 : roleFamily === 'C' ? 0.17 : roleFamily === 'D' ? 0.1 : 0.075)
      : marketPercentile >= 0.9
        ? initialBudget * (roleFamily === 'A' ? 0.16 : roleFamily === 'C' ? 0.13 : roleFamily === 'D' ? 0.085 : 0.06)
        : marketPercentile >= 0.8
          ? initialBudget * (roleFamily === 'A' ? 0.11 : roleFamily === 'C' ? 0.095 : roleFamily === 'D' ? 0.07 : 0.05)
          : 0;

  const rawSuggested = Math.max(1, Math.round(Math.max(budgetBased, valueFloor, performanceFloor, marketAnchor)));

  const hardCapBase = {
    P: 0.14,
    D: 0.18,
    C: 0.24,
    A: 0.3,
  }[roleFamily] || 0.18;

  const hardCap = hardCapBase * availabilityLift;

  const lowCap = {
    P: 0.012,
    D: 0.014,
    C: 0.018,
    A: 0.022,
  }[roleFamily] || 0.014;

  let suggestedBid = rawSuggested;

  if (qualityIndex < 0.22) {
    suggestedBid = Math.min(suggestedBid, Math.max(1, Math.round(initialBudget * lowCap)));
  }

  if (qualityIndex >= 0.92 && (roleFamily === 'A' || roleFamily === 'C')) {
    suggestedBid = Math.max(suggestedBid, Math.round(initialBudget * (roleFamily === 'A' ? 0.18 : 0.14)));
  }

  suggestedBid = Math.min(suggestedBid, Math.max(1, Math.round(initialBudget * hardCap)));
  suggestedBid = smartRoundCredits(suggestedBid);

  let band = 'consigliato';
  let note = '';
  if (qualityIndex >= 0.92) {
    band = 'aggressivo';
  } else if (qualityIndex < 0.22) {
    band = 'prudente';
  }

  const overMax = rawSuggested > Math.max(v(auction?.credits || 0), 1);

  return {
    suggestedBid,
    rawSuggested,
    band,
    note,
    overMax,
    deltaOverMax: 0,
    anchorValue,
    currentScore,
    previousScore,
    budgetCeiling: Math.max(1, Math.round(initialBudget * hardCap)),
    budgetHardCap: Math.max(1, Math.round(initialBudget * hardCap)),
    roleFamily,
    marketPercentile,
    familyPercentile,
    marketRank,
    teamCoeff,
  };
}

function getMantraPurchaseBucket(roleValue) {
  const tokens = getRoleTokens(roleValue, 'mantra').map((token) => String(token || '').toUpperCase());
  return tokens.includes('P') ? 'P' : 'M';
}

function PurchaseRow({ purchase, onEdit, onRemove, onOpenDetail, rolesMode, readOnly }) {
  const snap = purchase.playerSnapshot || {};
  const role = snap.role || purchase.role || 'A';
  const acquiredAt = formatDate(purchase.acquiredAt);

  return (
    <div className="purchase-row" onClick={readOnly ? undefined : onEdit} role="button" tabIndex={0}>
      <div className="purchase-row__left">
        <TeamBadge team={snap.team || purchase.playerTeam || '—'} size={32} />
        <div className="purchase-row__identity">
          <div className="purchase-row__headline">
            <div className="purchase-row__roles">
              {rolesMode === 'mantra'
                ? renderRoleBadges(snap.role || purchase.role || '—', rolesMode)
                : <RoleChip role={role} />}
            </div>

            <div className="purchase-row__nameWrap">
              <strong>{snap.name || purchase.playerName || '—'}</strong>

              <span className="purchase-row__team">
                {purchase.teamName || '—'}
              </span>

              <span className="purchase-row__timestamp">{acquiredAt}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="purchase-row__right">
        <strong className="purchase-row__credits">{purchase.credits} cr</strong>

        <div className="purchase-row__actions">
          <Tooltip
            as="button"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetail();
            }}
            content="Apri dettaglio"
          >
            <ExternalLink size={14} />
          </Tooltip>

          {!readOnly && (
            <Tooltip
              as="button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              content="Modifica acquisto"
            >
              <PencilLine size={14} />
            </Tooltip>
          )}

          {!readOnly && (
            <Tooltip
              as="button"
              type="button"
              className="danger"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              content="Rimuovi acquisto"
            >
              <Trash2 size={14} />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}

function PurchaseEditOverlay({ purchase, teamNames, onClose, onSave, onDelete, loading, rolesMode }) {
  const snap = purchase?.playerSnapshot || {};
  const [credits, setCredits] = useState('');
  const [teamName, setTeamName] = useState('');

  useEffect(() => {
    if (!purchase) return;
    setCredits(String(purchase.credits ?? ''));
    setTeamName(purchase.teamName || '');
  }, [purchase]);

  if (!purchase) return null;

  const role = snap.role || purchase.role || 'A';
  const roleText =
    rolesMode === 'mantra'
      ? getRoleTokens(role, 'mantra').map(getRoleFullLabel).join(' / ')
      : getRoleFullLabel(role);

  return (
    <div className="overlay-backdrop" onClick={onClose}>
      <div className="overlay-panel glass" onClick={(e) => e.stopPropagation()}>
        <div className="overlay-head">
          <div className="overlay-head__title">
            <AuctionTeamAvatar name={snap.team || purchase.playerTeam || purchase.playerName} />
            <div>
              <span>Modifica acquisto</span>
              <h3>{snap.name || purchase.playerName}</h3>
            </div>
          </div>

          <button className="overlay-close" type="button" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="overlay-edit-summary">
          <div>
            <span>Squadra originale</span>
            <strong>{snap.team || purchase.playerTeam}</strong>
          </div>
          <div>
            <span>Ruolo</span>
            <strong>{roleText}</strong>
          </div>
          <div>
            <span>Valutazione</span>
            <strong>{fmt(snap.value)}</strong>
          </div>
        </div>

        <div className="purchase-form purchase-form--overlay">
          <label>
            <span>Crediti spesi</span>
            <input
              type="number"
              min="1"
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              placeholder="Es. 24"
            />
          </label>

          <label>
            <span>Squadra vincente</span>
            <select value={teamName} onChange={(e) => setTeamName(e.target.value)}>
              <option value="">Seleziona squadra</option>
              {teamNames.map((team) => (
                <option key={team} value={team}>
                  {team}
                </option>
              ))}
            </select>
          </label>

          <div className="purchase-actions purchase-actions--overlay">
            <button
              className="confirm-btn"
              type="button"
              onClick={() => onSave(purchase, { credits: Number(credits), teamName })}
              disabled={loading || !credits || !teamName}
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="spin" />
                  Salvataggio...
                </>
              ) : (
                <>
                  <CheckCircle2 size={16} />
                  Salva modifiche
                </>
              )}
            </button>

            <button
              className="delete-btn"
              type="button"
              onClick={() => onDelete(purchase.playerId)}
              disabled={loading}
            >
              <Trash2 size={16} />
              Rimuovi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TeamRosterOverlay({
  teamName,
  purchases,
  stats,
  rosterLimits,
  credits,
  onClose,
  onOpenDetail,
  onEditPurchase,
  rolesMode,
}) {
  const grouped = useMemo(() => {
    if (rolesMode === 'mantra') {
      const byRole = { P: [], M: [] };

      (purchases || []).forEach((purchase) => {
        const roleValue = purchase?.playerSnapshot?.role || purchase?.role || 'A';
        const tokens = getRoleTokens(roleValue, 'mantra');
        const key = tokens.includes('P') ? 'P' : 'M';

        if (!byRole[key]) byRole[key] = [];
        byRole[key].push(purchase);
      });

      return byRole;
    }

    const byRole = { P: [], D: [], C: [], A: [] };

    (purchases || []).forEach((purchase) => {
      const roleValue = purchase?.playerSnapshot?.role || purchase?.role || 'A';
      const roleKey = String(roleValue || '').trim().toUpperCase() || 'A';

      if (!byRole[roleKey]) byRole[roleKey] = [];
      byRole[roleKey].push(purchase);
    });

    return byRole;
  }, [purchases, rolesMode]);

  const roleKeys = useMemo(() => {
    if (rolesMode !== 'mantra') return ROLE_ORDER;
    return ['P', 'M'];
  }, [rolesMode]);

  const [activeRole, setActiveRole] = useState(roleKeys[0] || 'P');

  useEffect(() => {
    setActiveRole(roleKeys[0] || 'P');
  }, [roleKeys]);

  if (!teamName) return null;

  const rolePlayers = grouped[activeRole] || [];
  const limit = rosterLimits?.[activeRole] ?? 0;
  const purchased = stats?.purchasedByRole?.[activeRole] ?? 0;

  return (
    <div className="overlay-backdrop" onClick={onClose}>
      <div className="overlay-panel overlay-panel--wide glass" onClick={(e) => e.stopPropagation()}>
        <div className="overlay-head">
          <div className="overlay-head__title">
            <AuctionTeamAvatar name={teamName} />
            <div>
              <span>Rosa squadra</span>
              <h3>{teamName}</h3>
            </div>
          </div>

          <button className="overlay-close" type="button" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="overlay-team-stats">
          <div>
            <span>Giocatori acquistati</span>
            <strong>{stats?.totalPlayers || purchases.length}</strong>
          </div>
          <div>
            <span>Residui</span>
            <strong>{stats?.remaining ?? credits} cr</strong>
          </div>
          <div>
            <span>Spesi</span>
            <strong>{stats?.spent || 0} cr</strong>
          </div>
        </div>

        <div className={`overlay-role-switcher ${rolesMode === 'mantra' ? 'overlay-role-switcher--mantra' : ''}`}>
          {roleKeys.map((role) => (
            <button
              key={role}
              type="button"
              className={activeRole === role ? 'is-active' : ''}
              onClick={() => setActiveRole(role)}
            >
              {rolesMode === 'mantra' ? (
                getOverlayRoleLabel(role, rolesMode)
              ) : (
                <>
                  <RoleChip role={role} />
                  <span>{ROLE_LABELS[role] || role}</span>
                </>
              )}
            </button>
          ))}
        </div>

        <section className="overlay-role-section overlay-role-section--single">
          <div className="overlay-role-section__head">
            <div className="overlay-role-section__headLeft">
              {rolesMode === 'mantra' ? (
                <span className={`overlay-role-badge overlay-role-badge--${activeRole === 'P' ? 'P' : 'M'}`}>
                  {getOverlayRoleLabel(activeRole, rolesMode)}
                </span>
              ) : (
                <RoleChip role={activeRole} />
              )}
              <span>
                {rolesMode === 'mantra'
                  ? getOverlayRoleLabel(activeRole, rolesMode)
                  : (ROLE_LABELS[activeRole] || activeRole)}
              </span>
            </div>

            <div className="overlay-role-section__meta">
              <strong>{rolesMode === 'classic' ? `${purchased}/${limit}` : `${rolePlayers.length}`}</strong>
              <span>{rolesMode === 'mantra' && activeRole === 'P' ? 'portieri' : 'liberi'}</span>
            </div>
          </div>

          <div className="overlay-role-list">
            {rolePlayers.length ? (
              rolePlayers.map((purchase) => {
                const snap = purchase.playerSnapshot || {};

                return (
                  <article
                    className="overlay-player-row overlay-player-row--compact"
                    key={`${purchase.playerId}-${purchase.teamName}`}
                  >
                    <div className="overlay-player-row__identity">
                      <TeamBadge team={snap.team || purchase.playerTeam || '—'} size={36} />
                      <div className="overlay-player-row__nameWrap">
                        <strong>{snap.name || purchase.playerName || '—'}</strong>
                        <span className="overlay-player-row__meta">
                          {rolesMode === 'mantra' ? getOverlayRoleLabel(activeRole, rolesMode) : ''}
                        </span>
                      </div>
                    </div>

                    <div className="overlay-player-row__credits">{purchase.credits} cr</div>

                    <div className="overlay-player-row__actions">
                      <Tooltip
                        as="button"
                        type="button"
                        className="overlay-player-row__actionBtn"
                        onClick={() => onOpenDetail(purchase.playerId)}
                        content="Apri dettaglio"
                        aria-label="Apri dettaglio"
                      >
                        <ExternalLink size={14} />
                      </Tooltip>

                      <Tooltip
                        as="button"
                        type="button"
                        className="overlay-player-row__actionBtn"
                        onClick={() => onEditPurchase(purchase)}
                        content="Modifica acquisto"
                        aria-label="Modifica acquisto"
                      >
                        <PencilLine size={14} />
                      </Tooltip>
                    </div>
                  </article>
                );
              })
            ) : (
              <div className="overlay-empty">Nessun giocatore in questo ruolo.</div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function TeamSummaryRow({ team, stats, rosterLimits, isMine, rolesMode, onOpen, readOnly }) {
  const creditsRemaining = v(stats?.remaining || 0);

  const remainingMinSlots =
    rolesMode === 'mantra'
      ? Number(stats?.remainingMinByRole?.P ?? 0) + Number(stats?.remainingMinByRole?.M ?? 0)
      : sumRemainingMinSlots(stats);

  const maxBidReal = getTeamMaxBidReal(stats, rosterLimits, rolesMode);

  // Zero flessibilità: il residuo copre al massimo 1 credito per ogni slot
  // ancora OBBLIGATORIO da riempire per il minimo di rosa (nessun margine
  // per rilanciare senza rinunciare a qualcun altro).
  const isCritical = remainingMinSlots > 0 && creditsRemaining <= remainingMinSlots;

  // Residuo rispetto al limite MASSIMO impostato per l'asta: parte dal
  // valore massimo del ruolo e scende di 1 ad ogni acquisto di quel ruolo.
  const buildRoleChip = (key, label, title) => {
    const maxRemaining = v(stats?.remainingByRole?.[key] ?? rosterLimits?.[key] ?? 0);

    return {
      key,
      label,
      title,
      maxRemaining,
      display: String(maxRemaining),
      fullTitle: title,
    };
  };

  const roleChips =
    rolesMode === 'mantra'
      ? [
        buildRoleChip('P', 'P', 'Portieri residui'),
        buildRoleChip('M', 'Mov', 'Giocatori di movimento residui'),
      ]
      : [
        buildRoleChip('P', 'P', 'Portieri residui'),
        buildRoleChip('D', 'D', 'Difensori residui'),
        buildRoleChip('C', 'C', 'Centrocampisti residui'),
        buildRoleChip('A', 'A', 'Attaccanti residui'),
      ];

  return (
    <button
      type="button"
      className={`team-summary-card ${isMine ? 'is-mine' : ''} ${isCritical ? 'is-critical' : ''}`.trim()}
      onClick={readOnly ? undefined : onOpen}
      style={readOnly ? { cursor: 'default' } : undefined}
    >
      <div className="team-summary-card__row1">
        <div className="team-summary-card__identity">
          <AuctionTeamAvatar name={team} />
          <strong className="team-summary-card__name">{team}</strong>
          {isMine ? <span className="team-summary-row__you">TU</span> : null}
        </div>

        <span className="team-summary-card__budgetStat">
          <strong>{creditsRemaining}</strong> cr res.
        </span>
        <span className="team-summary-card__budgetStat team-summary-card__budgetStat--maxbid">
          <strong>{maxBidReal}</strong> cr max
        </span>
      </div>

      <div className="team-summary-card__roles">
        {roleChips.map((chip) => (
          <Tooltip
            as="span"
            key={chip.key}
            className={`team-summary-role team-summary-role--${chip.key} ${chip.maxRemaining === 0 ? 'is-locked' : ''}`.trim()}
            content={chip.fullTitle}
            aria-label={`${chip.fullTitle}: ${chip.display}`}
          >
            <em>{chip.label}</em>
            {chip.display}
          </Tooltip>
        ))}
      </div>
    </button>
  );
}

function buildPurchasePreview({
  auction,
  rosterLimits,
  selectedPlayer,
  selectedTeamStats,
  selectedPurchase,
  selectedPurchaseSameTeam,
  selectedRole,
  spentCredits,
  effectiveRemainingRoleSlots,
  rolesMode,
  marketContext,
  teamEconomy,
  teamNames,
}) {
  if (!selectedPlayer || !selectedTeamStats) return null;

  const enteredCredits = Number(spentCredits);
  const isValidCredits = Number.isFinite(enteredCredits) && enteredCredits > 0;
  const initialCredits = v(auction?.credits || 0);

  const selectedSlotKey = getPurchaseRoleSlot(selectedPlayer?.role, rolesMode);
  const selectedPurchaseSlotKey = selectedPurchase
    ? getPurchaseRoleSlot(
      selectedPurchase?.playerSnapshot?.role ?? selectedPurchase?.role ?? 'A',
      rolesMode
    )
    : selectedSlotKey;

  const effectiveRemainingCredits = selectedPurchaseSameTeam
    ? v(selectedTeamStats.remaining) + v(selectedPurchase?.credits)
    : v(selectedTeamStats.remaining);

  // Slot ancora OBBLIGATORI da riempire per raggiungere il minimo di rosa
  // (non la capienza residua fino al massimo): guida quanti crediti vanno
  // tenuti da parte per acquisti futuri necessari.
  const totalRemainingSlots =
    rolesMode === 'classic'
      ? sumRemainingMinSlots(selectedTeamStats)
      : Number(selectedTeamStats?.remainingMinByRole?.P ?? 0) +
      Number(selectedTeamStats?.remainingMinByRole?.M ?? 0);

  // Capienza residua fino al MASSIMO per il ruolo selezionato: se è 0 non si
  // può comprare, indipendentemente dal minimo.
  const effectiveRemainingSelectedRoleSlots =
    rolesMode === 'classic'
      ? Number(
        selectedTeamStats?.remainingByRole?.[selectedSlotKey] ??
        rosterLimits?.[selectedSlotKey] ??
        0
      )
      : Number(selectedTeamStats?.remainingByRole?.[selectedSlotKey] ?? 0);

  // Il ruolo che si sta comprando ORA deve ancora raggiungere il proprio
  // minimo? Solo in quel caso questo acquisto stesso copre uno degli
  // obblighi residui, quindi la riserva per gli ALTRI è totalRemainingSlots
  // - 1. Se invece il minimo di quel ruolo è già soddisfatto (si sta
  // comprando un giocatore "extra" fino al massimo), questo acquisto non
  // riduce nulla: va riservato l'intero totalRemainingSlots per gli
  // obblighi ancora scoperti sugli altri ruoli.
  const selectedSlotOwesMinimum = Number(selectedTeamStats?.remainingMinByRole?.[selectedSlotKey] ?? 0) > 0;

  const reserveSlotsAfterPurchase = selectedPurchaseSameTeam
    ? totalRemainingSlots
    : selectedSlotOwesMinimum
      ? Math.max(totalRemainingSlots - 1, 0)
      : totalRemainingSlots;

  const maxBid = Math.max(effectiveRemainingCredits - reserveSlotsAfterPurchase, 0);

  const remainingAfterPurchase = isValidCredits
    ? Math.max(effectiveRemainingCredits - enteredCredits, 0)
    : effectiveRemainingCredits;

  const percentOfInitialBudget = initialCredits ? (enteredCredits / initialCredits) * 100 : 0;
  const percentOfTeamBudget = effectiveRemainingCredits ? (enteredCredits / effectiveRemainingCredits) * 100 : 0;

  const suggestedMeta = getSmartSuggestedBid({
    player: selectedPlayer,
    auction,
    rolesMode,
    marketContext,
  });

  const reactiveMeta = getReactivePrice({
    player: selectedPlayer,
    auction,
    rolesMode,
    teamEconomy,
    teamNames,
    rosterLimits,
    suggestedBid: suggestedMeta.suggestedBid,
  });

  let status = '';
  let canBuy = true;

  if (!isValidCredits) {
    status = 'Inserisci una puntata valida.';
    canBuy = false;
  } else if (effectiveRemainingSelectedRoleSlots <= 0) {
    status = `Nessuno slot residuo per ${getSlotLabel(selectedSlotKey, rolesMode)}.`;
    canBuy = false;
  } else if (enteredCredits > effectiveRemainingCredits) {
    status = `Servono ancora ${Math.max(enteredCredits - effectiveRemainingCredits, 0)} crediti.`;
    canBuy = false;
  } else if (enteredCredits > maxBid) {
    status = `Puntata troppo alta: devi tenere liberi almeno ${reserveSlotsAfterPurchase} crediti per gli slot rimanenti.`;
    canBuy = false;
  } else {
    status = 'Acquisto compatibile con la rosa selezionata.';
  }

  return {
    enteredCredits,
    initialCredits,
    effectiveRemainingCredits,
    remainingAfterPurchase,
    percentOfInitialBudget,
    percentOfTeamBudget,
    totalRemainingSlots,
    reserveSlotsAfterPurchase,
    maxBid,
    canBuy,
    status,
    selectedSlotKey,
    selectedPurchaseSlotKey,
    effectiveRemainingSelectedRoleSlots,
    suggestedBid: suggestedMeta.suggestedBid,
    rawSuggested: suggestedMeta.rawSuggested,
    suggestedBand: suggestedMeta.band,
    suggestedNote: suggestedMeta.note,
    suggestedOverMax: suggestedMeta.overMax,
    suggestedDeltaOverMax: suggestedMeta.deltaOverMax,
    reactiveBid: reactiveMeta.reactiveBid,
    reactiveRatio: reactiveMeta.liquidityRatio,
    reactiveSampleSize: reactiveMeta.sampleSize,
    anchorValue: suggestedMeta.anchorValue,
    currentScore: suggestedMeta.currentScore,
    previousScore: suggestedMeta.previousScore,
    roleFamily: suggestedMeta.roleFamily,
    marketPercentile: suggestedMeta.marketPercentile,
    familyPercentile: suggestedMeta.familyPercentile,
    marketRank: suggestedMeta.marketRank,
    budgetCeiling: suggestedMeta.budgetCeiling,
    budgetHardCap: suggestedMeta.budgetHardCap,
    teamCoeff: suggestedMeta.teamCoeff,
  };
}

function PurchaseOutcomeOverlay({ outcome, onClose }) {
  if (!outcome) return null;

  return (
    <div className="purchase-outcome-backdrop" onClick={onClose}>
      <div
        className={`purchase-outcome-card glass purchase-outcome-card--${outcome.kind}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="purchase-outcome-card__eyebrow">
          {outcome.kind === 'success' ? 'Esito positivo' : 'Acquisto non possibile'}
        </div>

        <h3>{outcome.title}</h3>
        <p>{outcome.message}</p>

        <div className="purchase-outcome-card__actions">
          <button type="button" onClick={onClose}>
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}

// Etichette con articolo per la frase "<Squadra> ha terminato <ruolo>": P/D/C/A
// sono le stesse chiavi di remainingByRole in modalità classic, M è il bucket
// "giocatori di movimento" usato in modalità mantra.
const ROLE_COMPLETION_LABELS = {
  P: 'i portieri',
  D: 'i difensori',
  C: 'i centrocampisti',
  A: 'gli attaccanti',
  M: 'i giocatori di movimento',
};

const AUCTION_TOAST_TTL = { team: 7000, league: 10000, roster: 9000, 'key-sniped': 10000 };

function AuctionToast({ toast, onDismiss }) {
  useEffect(() => {
    const timer = window.setTimeout(() => onDismiss(toast.id), AUCTION_TOAST_TTL[toast.kind] || 6000);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast.id]);

  const roleLabel = ROLE_COMPLETION_LABELS[toast.role] || 'i giocatori di questo ruolo';

  let icon = null;
  let body = null;

  if (toast.kind === 'league') {
    icon = <span className="auction-toast__icon"><Flag size={16} /></span>;
    body = (
      <>
        <strong>Ruolo esaurito in lega</strong>
        <span>Tutte le squadre hanno terminato {roleLabel}</span>
      </>
    );
  } else if (toast.kind === 'roster') {
    icon = <span className="auction-toast__icon"><CheckCircle2 size={16} /></span>;
    body = (
      <>
        <strong>{toast.team}</strong>
        <span>ha completato la rosa ({toast.totalPlayers}/{toast.rosterSize} giocatori)</span>
      </>
    );
  } else if (toast.kind === 'key-sniped') {
    icon = <span className="auction-toast__icon"><KeyRound size={16} /></span>;
    const roleLabelLower = (CLASSIC_ROLE_LABELS[toast.role] || 'giocatori').toLowerCase();
    body = (
      <>
        <strong>{toast.playerName} preso da {toast.team}</strong>
        <span>Restano {toast.availableKeyCount} {roleLabelLower} chiave svincolati</span>
      </>
    );
  } else {
    icon = <AuctionTeamAvatar name={toast.team} />;
    body = (
      <>
        <strong>{toast.team}</strong>
        <span>ha terminato {roleLabel}</span>
      </>
    );
  }

  return (
    <div className={`auction-toast auction-toast--${toast.kind}`}>
      {icon}
      <div className="auction-toast__body">{body}</div>
      <button type="button" className="auction-toast__close" onClick={() => onDismiss(toast.id)} aria-label="Chiudi avviso">
        <X size={14} />
      </button>
    </div>
  );
}

function AuctionToastStack({ toasts, onDismiss }) {
  if (!toasts.length) return null;

  return (
    <div className="auction-toast-stack">
      {toasts.map((toast) => (
        <AuctionToast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

export default function AuctionMarketTab({
  auction,
  players,
  purchases,
  teamNames,
  teamEconomy,
  rosterLimits,
  rosterMinLimits,
  rolesMode = 'classic',
  marketPmaByPlayerId = {},
  playerCategoriesById = {},
  playerTiersById = {},
  playerMaxSpendById = {},
  initialSelectedPlayerId = null,
  initialWinningTeam = '',
  jumpToken = 0,
  onPurchase,
  onRemovePurchase,
  onSaveEditedPurchase,
  onOpenPlayerDetail,
  onOpenTeamOverlay,
  onAuctionPlayersCountChange,
  playerNotesById = {},
  playerBackupGoalkeepersById = {},
  playProbabilityById = {},
  readOnly = false,
  customCategoryLabel,
  onSelectionChange,
}) {
  const storageKey = auction?.id ? `${FILTER_STORAGE_PREFIX}:${auction.id}` : null;

  const [search, setSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [roleFilters, setRoleFilters] = useState([]);
  const [sortMode, setSortMode] = useState('value_desc');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState(() =>
    initialSelectedPlayerId ? String(initialSelectedPlayerId) : null
  );
  const [spentCredits, setSpentCredits] = useState('');
  const [winningTeam, setWinningTeam] = useState(
    initialWinningTeam || auction?.myTeamName || teamNames?.[0] || ''
  );
  const [purchaseError, setPurchaseError] = useState('');
  const [busy, setBusy] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [activeTeamOverlay, setActiveTeamOverlay] = useState(null);
  const [purchaseOutcome, setPurchaseOutcome] = useState(null);
  const [optimisticPurchasedIds, setOptimisticPurchasedIds] = useState(() => new Set());

  const rootRef = useRef(null);
  const searchInputRef = useRef(null);
  const creditsInputRef = useRef(null);
  const previousRemainingByRoleRef = useRef(null);
  const leagueCompletedRolesRef = useRef(new Set());
  const rosterCompletedTeamsRef = useRef(new Set());
  const seenPurchasedPlayerIdsRef = useRef(null);
  const [auctionToasts, setAuctionToasts] = useState([]);

  // Digitare senza aver cliccato in un campo: le cifre finiscono sempre
  // sull'input crediti, le lettere sulla ricerca giocatore — utile durante
  // un'asta live per non dover cliccare ogni volta prima di scrivere.
  // Non interviene se si sta già scrivendo in un altro campo (input/textarea/
  // select/contentEditable), con tasti modificatori (scorciatoie del
  // browser) o quando il tab Market non è quello visibile (altri tab restano
  // montati ma nascosti con display:none).
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key.length !== 1) return;

      const active = document.activeElement;
      const isEditingElsewhere = active && (
        active.tagName === 'INPUT' ||
        active.tagName === 'TEXTAREA' ||
        active.tagName === 'SELECT' ||
        active.isContentEditable
      );
      if (isEditingElsewhere) return;

      if (!rootRef.current || rootRef.current.offsetParent === null) return;

      if (/^[0-9]$/.test(event.key)) {
        if (readOnly || !creditsInputRef.current) return;
        event.preventDefault();
        setSpentCredits((prev) => `${prev || ''}${event.key}`);
        creditsInputRef.current.focus();
        return;
      }

      if (/^[a-zà-öø-ÿ]$/i.test(event.key)) {
        if (!searchInputRef.current) return;
        event.preventDefault();
        setSearch((prev) => `${prev || ''}${event.key}`);
        searchInputRef.current.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [readOnly]);

  const purchasedMap = useMemo(() => {
    const map = new Map();
    (purchases || [])
      .filter((p) => !p.deleted)
      .forEach((p) => map.set(String(p.playerId), p));
    return map;
  }, [purchases]);

  useEffect(() => {
    if (!storageKey) return;

    const stored = loadStoredFilters(storageKey);
    if (!stored) return;

    if (typeof stored.search === 'string') setSearch(stored.search);
    if (typeof stored.teamFilter === 'string') setTeamFilter(stored.teamFilter);
    if (typeof stored.categoryFilter === 'string') setCategoryFilter(stored.categoryFilter);
    if (typeof stored.tierFilter === 'string') setTierFilter(stored.tierFilter);
    if (Array.isArray(stored.roleFilters)) setRoleFilters(stored.roleFilters);
    if (typeof stored.sortMode === 'string') setSortMode(stored.sortMode);
    if (typeof stored.selectedPlayerId === 'string' || stored.selectedPlayerId === null) {
      setSelectedPlayerId(stored.selectedPlayerId);
    }
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey) return;

    saveStoredFilters(storageKey, {
      search,
      teamFilter,
      categoryFilter,
      tierFilter,
      roleFilters,
      sortMode,
      selectedPlayerId,
    });
  }, [storageKey, search, teamFilter, categoryFilter, tierFilter, roleFilters, sortMode, selectedPlayerId]);

  const availablePlayers = useMemo(() => {
    return (players || []).filter(
      (player) => !purchasedMap.has(String(player.id)) && !optimisticPurchasedIds.has(String(player.id))
    );
  }, [players, purchasedMap, optimisticPurchasedIds]);

  const marketContext = useMemo(() => buildMarketContext(availablePlayers, rolesMode), [availablePlayers, rolesMode]);

  const teamOptions = useMemo(() => {
    const set = new Set();
    availablePlayers.forEach((player) => {
      if (player?.team) set.add(String(player.team));
    });
    return ['all', ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [availablePlayers]);

  const categoryOptions = useMemo(() => {
    const present = new Set();
    availablePlayers.forEach((player) => {
      const category = getPlayerCategory(player, playerCategoriesById);
      present.add(category);
    });

    const preferred = CATEGORY_FILTERS.filter((category) => category === 'all' || present.has(category));
    if (!preferred.includes('all')) preferred.unshift('all');
    return preferred;
  }, [availablePlayers, playerCategoriesById]);

  const tierOptions = useMemo(() => {
    const present = new Set();
    availablePlayers.forEach((player) => {
      const tier = getPlayerTier(player, playerTiersById);
      present.add(tier ? String(tier) : 'none');
    });

    const preferred = TIER_FILTERS.filter((tier) => tier === 'all' || present.has(tier));
    if (!preferred.includes('all')) preferred.unshift('all');
    return preferred;
  }, [availablePlayers, playerTiersById]);

  const selectedPlayer = useMemo(
    () => players.find((p) => String(p.id) === String(selectedPlayerId)) || null,
    [players, selectedPlayerId]
  );

  const selectedPurchase = selectedPlayer ? purchasedMap.get(String(selectedPlayer.id)) : null;
  const selectedRole = getPrimaryRoleValue(selectedPlayer?.role, rolesMode);
  const selectedTeamStats = winningTeam ? teamEconomy?.[winningTeam] : null;
  const selectedPurchaseSameTeam = Boolean(selectedPurchase && selectedPurchase.teamName === winningTeam);
  const selectedCategory = selectedPlayer ? getPlayerCategory(selectedPlayer, playerCategoriesById) : 'none';
  const selectedPlayerNote = selectedPlayer
  ? String(playerNotesById?.[String(selectedPlayer.id)] || '').trim()
  : '';

  const selectedPlayerNotePreview =
    selectedPlayerNote.length > 110
      ? `${selectedPlayerNote.slice(0, 110)}…`
      : selectedPlayerNote;

  // Solo per i portieri, e solo se valorizzato (vedi StrategyEditPage.js
  // colonna "Por riserva" + importStrategyIntoAuction): nome libero, non un
  // id giocatore.
  const selectedGoalkeeperBackup = selectedPlayer
    ? String(playerBackupGoalkeepersById?.[String(selectedPlayer.id)] || '').trim()
    : '';
  const hasSelectedGoalkeeperBackup = getRoleFamily(selectedPlayer?.role, rolesMode) === 'P' && Boolean(selectedGoalkeeperBackup);

  // Riflette la selezione corrente (giocatore + crediti impostati) verso il
  // genitore: serve al riquadro "in evidenza" della visualizzazione compatta
  // nel tab Squadre — anche in un'ALTRA scheda/dispositivo, perché il
  // genitore la sincronizza su Firestore (vedi AuctionDetailPage). Solo
  // pochi campi piatti, mai l'oggetto giocatore intero: deve poter finire
  // così com'è in un documento Firestore.
  useEffect(() => {
    if (typeof onSelectionChange !== 'function') return;
    onSelectionChange(
      selectedPlayer
        ? {
            player: {
              id: String(selectedPlayer.id),
              name: selectedPlayer.name || '',
              team: selectedPlayer.team || '',
              role: selectedPlayer.role || '',
            },
            credits: spentCredits || '',
            teamName: winningTeam || '',
          }
        : null
    );
  }, [selectedPlayer, spentCredits, winningTeam, onSelectionChange]);

  useEffect(() => {
    if (initialSelectedPlayerId !== null && initialSelectedPlayerId !== undefined) {
      setSelectedPlayerId(String(initialSelectedPlayerId));
      setRoleFilters([]);
    }
  }, [initialSelectedPlayerId, jumpToken]);

  useEffect(() => {
    if (initialWinningTeam) {
      setWinningTeam(initialWinningTeam);
    }
  }, [initialWinningTeam, jumpToken]);

  useEffect(() => {
    if (!selectedPlayer) return;
    const existing = purchasedMap.get(String(selectedPlayer.id));
    setSpentCredits(existing?.credits ? String(existing.credits) : '');
    setWinningTeam(existing?.teamName || initialWinningTeam || auction?.myTeamName || teamNames?.[0] || '');
  }, [selectedPlayer, purchasedMap, auction?.myTeamName, teamNames, initialWinningTeam]);

  useEffect(() => {
    setOptimisticPurchasedIds((prev) => {
      if (!prev.size) return prev;

      const next = new Set(prev);
      for (const id of prev) {
        if (purchasedMap.has(String(id))) {
          next.delete(String(id));
        }
      }
      return next.size === prev.size ? prev : next;
    });
  }, [purchasedMap]);

  const filteredPlayers = useMemo(() => {
    let list = [...availablePlayers];

    if (search.trim()) {
      const s = normalizeText(search);
      list = list.filter((p) => normalizeText(`${p.name} ${p.team}`).includes(s));
    }

    if (teamFilter !== 'all') {
      list = list.filter((p) => String(p.team || '') === String(teamFilter));
    }

    if (categoryFilter !== 'all') {
      list = list.filter((p) => getPlayerCategory(p, playerCategoriesById) === categoryFilter);
    }

    if (tierFilter !== 'all') {
      const wantTier = tierFilter === 'none' ? 0 : Number(tierFilter);
      list = list.filter((p) => getPlayerTier(p, playerTiersById) === wantTier);
    }

    if (roleFilters.length) {
      list = list.filter((p) => playerMatchesRoles(p, roleFilters, rolesMode));
    }

    // Pre-calcola delta PMA (suggeritoBid - PMA) per ogni giocatore visibile
    let pmaDeltaMap = null;
    if (sortMode === 'pma_delta_desc') {
      pmaDeltaMap = new Map();
      list.forEach((p) => {
        const pmaAvg = marketPmaByPlayerId?.[String(p.id)]?.average;
        if (pmaAvg === null || pmaAvg === undefined) {
          pmaDeltaMap.set(String(p.id), -Infinity);
          return;
        }
        const { suggestedBid } = getSmartSuggestedBid({ player: p, auction, rolesMode, marketContext });
        pmaDeltaMap.set(String(p.id), suggestedBid - v(pmaAvg));
      });
    }

    const sortMap = {
      value_desc: (a, b) => v(b.value) - v(a.value),
      value_asc: (a, b) => v(a.value) - v(b.value),
      mv_desc: (a, b) => v(b.markavg) - v(a.markavg),
      fmv_desc: (a, b) => v(b.fmarkavg) - v(a.fmarkavg),
      caps_desc: (a, b) => v(b.caps) - v(a.caps),
      goals_desc: (a, b) => v(b.goals) - v(a.goals),
      assists_desc: (a, b) => v(b.assists) - v(a.assists),
      // Fascia 1 (top) prima, poi 2..5; chi non ha fascia assegnata va in fondo.
      tier_asc: (a, b) => {
        const ta = getPlayerTier(a, playerTiersById) || 6;
        const tb = getPlayerTier(b, playerTiersById) || 6;
        return ta - tb || v(b.value) - v(a.value);
      },
      pma_delta_desc: (a, b) => (pmaDeltaMap.get(String(b.id)) ?? -Infinity) - (pmaDeltaMap.get(String(a.id)) ?? -Infinity),
      playprob_desc: (a, b) =>
        getPlayProbabilitySortValue(playProbabilityById?.[String(b.id)]) -
        getPlayProbabilitySortValue(playProbabilityById?.[String(a.id)]),
      hidden_gems: (a, b) => {
        const gem = (p) => {
          const caps = Math.max(v(p.caps), 1);
          const perf = getPerformanceCoreScore(p, getRoleFamily(p.role, rolesMode));
          return (perf / Math.max(v(p.value), 1)) * Math.log1p(caps);
        };
        return gem(b) - gem(a);
      },
      name: (a, b) => a.name.localeCompare(b.name),
      new_first: (a, b) => {
        const aNew = !a.previousSeasonStats ? 0 : 1;
        const bNew = !b.previousSeasonStats ? 0 : 1;
        return aNew - bNew || v(b.value) - v(a.value);
      },
    };

    list.sort(sortMap[sortMode] || sortMap.value_desc);
    return list;
  }, [availablePlayers, search, teamFilter, categoryFilter, tierFilter, roleFilters, sortMode, rolesMode, playerCategoriesById, playerTiersById, marketPmaByPlayerId, auction, marketContext, playProbabilityById]);

  useEffect(() => {
    if (!filteredPlayers.length) return;
    if (selectedPlayerId && filteredPlayers.some((p) => String(p.id) === String(selectedPlayerId))) return;
    setSelectedPlayerId(String(filteredPlayers[0].id));
  }, [filteredPlayers, selectedPlayerId]);

  const effectiveRemainingCredits = useMemo(() => {
    const baseRemaining = selectedTeamStats?.remaining ?? Number(auction?.credits || 0);
    if (selectedPurchaseSameTeam) return baseRemaining + v(selectedPurchase?.credits);
    return baseRemaining;
  }, [selectedTeamStats, auction, selectedPurchaseSameTeam, selectedPurchase]);

  const effectiveRemainingRoleSlots = useMemo(() => {
    if (rolesMode !== 'classic') return Number.POSITIVE_INFINITY;

    const base = selectedTeamStats?.remainingByRole?.[selectedRole] ?? rosterLimits[selectedRole] ?? 0;

    if (
      selectedPurchaseSameTeam &&
      (selectedPurchase?.playerSnapshot?.role || selectedPurchase?.role) === selectedRole
    ) {
      return base + 1;
    }

    return base;
  }, [selectedTeamStats, selectedPurchaseSameTeam, selectedPurchase, selectedRole, rosterLimits, rolesMode]);

  const purchasePreview = useMemo(
    () =>
      buildPurchasePreview({
        auction,
        rosterLimits,
        selectedPlayer,
        selectedTeamStats,
        selectedPurchase,
        selectedPurchaseSameTeam,
        selectedRole,
        spentCredits,
        effectiveRemainingRoleSlots,
        rolesMode,
        marketContext,
        teamEconomy,
        teamNames,
      }),
    [
      auction,
      selectedPlayer,
      selectedTeamStats,
      selectedPurchase,
      selectedPurchaseSameTeam,
      selectedRole,
      spentCredits,
      effectiveRemainingRoleSlots,
      rolesMode,
      marketContext,
      teamEconomy,
      teamNames,
    ]
  );

  const purchasePriceToCheck = useMemo(() => {
    const entered = Number(spentCredits);

    if (Number.isFinite(entered) && entered > 0) {
      return entered;
    }

    return Number(purchasePreview?.enteredCredits || purchasePreview?.suggestedBid || 0);
  }, [spentCredits, purchasePreview]);

  const roleFilterOptions = useMemo(() => {
    return rolesMode === 'mantra' ? MANTRA_ROLE_FILTERS : ['all', 'P', 'D', 'C', 'A'];
  }, [rolesMode]);

  // "Tutti" azzera la selezione; ogni altro ruolo si aggiunge/rimuove dalla
  // selezione multipla (OR tra i ruoli scelti, vedi playerMatchesRoles).
  const toggleRoleFilter = (role) => {
    if (role === 'all') {
      setRoleFilters([]);
      return;
    }
    setRoleFilters((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  };

  const handlePurchaseForTeam = async (targetTeamName, skipTeamChecks = false) => {
    setPurchaseError('');

    if (!selectedPlayer || !auction?.id) {
      setPurchaseOutcome({
        kind: 'error',
        title: 'Campi mancanti',
        message: 'Seleziona giocatore',
      });
      return;
    }

    const creditsNumber = Number(
      spentCredits || (skipTeamChecks ? 1 : 0)
    );

    if (!skipTeamChecks && (Number.isNaN(creditsNumber) || creditsNumber <= 0)) {
      setPurchaseOutcome({
        kind: 'error',
        title: 'Crediti non validi',
        message: 'Inserisci un numero valido di crediti.',
      });
      return;
    }

    if (!skipTeamChecks) {
      if (!targetTeamName) {
        setPurchaseOutcome({
          kind: 'error',
          title: 'Squadra mancante',
          message: 'Seleziona una squadra vincente.',
        });
        return;
      }

      if (purchasePreview && !purchasePreview.canBuy) {
        setPurchaseError(purchasePreview.status);
        setPurchaseOutcome({
          kind: 'error',
          title: 'Acquisto non possibile',
          message: purchasePreview.status,
        });
        return;
      }

      if (creditsNumber > effectiveRemainingCredits) {
        const msg = `Crediti insufficienti: restano ${effectiveRemainingCredits} crediti.`;
        setPurchaseError(msg);
        setPurchaseOutcome({
          kind: 'error',
          title: 'Crediti insufficienti',
          message: msg,
        });
        return;
      }

      if (purchasePreview && creditsNumber > purchasePreview.maxBid) {
        const msg = `Puntata troppo alta: la massima puntata consentita è ${purchasePreview.maxBid} crediti.`;
        setPurchaseError(msg);
        setPurchaseOutcome({
          kind: 'error',
          title: 'Puntata troppo alta',
          message: msg,
        });
        return;
      }
    }

    setBusy(true);
    try {
      if (selectedPlayer?.id !== undefined && selectedPlayer?.id !== null) {
        setOptimisticPurchasedIds((prev) => new Set(prev).add(String(selectedPlayer.id)));
      }

      await onPurchase({
        player: selectedPlayer,
        teamName: targetTeamName,
        credits: creditsNumber,
        existingPurchase: selectedPurchase,
      });

      setPurchaseOutcome(null);
      setSearch('');

    } catch {
      setPurchaseOutcome({
        kind: 'error',
        title: 'Errore durante l’acquisto',
        message: 'Non sono riuscito a completare l’operazione.',
      });
    } finally {
      setBusy(false);
    }
  };

  const handlePurchase = () => handlePurchaseForTeam(winningTeam, false);
  const handlePurchaseAsOtherTeam = () => handlePurchaseForTeam(HIDDEN_TEAM_NAME, true);

  const validateEditedPurchase = (purchase, values) => {
    const newTeam = values?.teamName;
    const newCredits = Number(values?.credits);

    if (!Number.isFinite(newCredits) || newCredits <= 0) {
      return {
        title: 'Crediti non validi',
        message: 'Inserisci un numero valido di crediti.',
      };
    }

    if (!newTeam || newTeam === HIDDEN_TEAM_NAME) return null;

    const sameTeam = newTeam === purchase?.teamName;
    const stats = finalTeamEconomy?.[newTeam];
    const slotKey = getPurchaseRoleSlot(
      purchase?.playerSnapshot?.role ?? purchase?.role ?? 'A',
      rolesMode
    );

    if (!sameTeam) {
      const remainingRoleSlots = Number(stats?.remainingByRole?.[slotKey] ?? 0);

      if (remainingRoleSlots <= 0) {
        return {
          title: 'Acquisto non possibile',
          message: `Nessuno slot residuo per ${getSlotLabel(slotKey, rolesMode)} nella squadra ${newTeam}.`,
        };
      }
    }

    const effectiveRemaining = sameTeam
      ? v(stats?.remaining) + v(purchase?.credits)
      : v(stats?.remaining);

    if (newCredits > effectiveRemaining) {
      return {
        title: 'Crediti insufficienti',
        message: `Crediti insufficienti: alla squadra ${newTeam} restano ${effectiveRemaining} crediti.`,
      };
    }

    const totalRemainingSlots =
      rolesMode === 'classic'
        ? sumRemainingMinSlots(stats)
        : Number(stats?.remainingMinByRole?.P ?? 0) + Number(stats?.remainingMinByRole?.M ?? 0);

    // Come in buildPurchasePreview: sconto di 1 solo se il ruolo di questo
    // giocatore deve ancora raggiungere il proprio minimo nella squadra di
    // destinazione (in quel caso l'acquisto copre esso stesso uno degli
    // obblighi residui); altrimenti riserva piena.
    const slotOwesMinimum = Number(stats?.remainingMinByRole?.[slotKey] ?? 0) > 0;
    const reserveSlots = sameTeam
      ? totalRemainingSlots
      : slotOwesMinimum
        ? Math.max(totalRemainingSlots - 1, 0)
        : totalRemainingSlots;
    const maxBid = Math.max(effectiveRemaining - reserveSlots, 0);

    if (newCredits > maxBid) {
      return {
        title: 'Puntata troppo alta',
        message: `Puntata troppo alta: la massima puntata consentita per ${newTeam} è ${maxBid} crediti.`,
      };
    }

    return null;
  };

  const handleSaveEditedPurchase = async (purchase, values) => {
    const validationError = validateEditedPurchase(purchase, values);
    if (validationError) {
      setPurchaseOutcome({ kind: 'error', ...validationError });
      return;
    }

    setBusy(true);
    try {
      await onSaveEditedPurchase(purchase, values);
      setEditingPurchase(null);
    } finally {
      setBusy(false);
    }
  };

  const openDetailFromPlayer = (playerId) => {
    if (typeof onOpenPlayerDetail === 'function') {
      onOpenPlayerDetail(playerId);
    }
  };

  const computedTeamEconomy = useMemo(
    () =>
      buildTeamEconomy({
        teamNames,
        purchases,
        auction,
        rosterLimits,
        rosterMinLimits,
        rolesMode,
      }),
    [teamNames, purchases, auction, rosterLimits, rosterMinLimits, rolesMode]
  );

  const finalTeamEconomy = teamEconomy && Object.keys(teamEconomy || {}).length ? teamEconomy : computedTeamEconomy;

  // Overlay "<Squadra> ha terminato i <ruolo>": si accorge quando una
  // squadra esaurisce gli slot disponibili per un ruolo (remainingByRole
  // passa da >0 a 0) confrontando lo snapshot precedente con quello
  // ricalcolato ad ogni acquisto. Il primo calcolo dopo il mount/cambio asta
  // fa solo da base di partenza: nessun avviso per ruoli già completi.
  // In più: quando l'ultima squadra rimasta esaurisce un ruolo, TUTTE le
  // squadre lo hanno terminato → avviso "di lega" (colore diverso); e quando
  // TUTTI i ruoli di una squadra sono esauriti insieme, la rosa di quella
  // squadra è completa → avviso "rosa completa" (altro colore ancora).
  // Entrambi scattano una tantum finché non tornano a non essere più veri
  // (es. un acquisto cancellato/modificato riapre il ruolo/la rosa).
  useEffect(() => {
    const previous = previousRemainingByRoleRef.current;
    const isFirstRun = previous === null;
    const nextSnapshot = {};
    const newlyCompleted = [];
    const newlyCompletedRosters = [];

    Object.entries(finalTeamEconomy || {}).forEach(([team, stats]) => {
      const remainingByRole = stats?.remainingByRole || {};
      nextSnapshot[team] = { ...remainingByRole };

      const remainingValues = Object.values(remainingByRole).map(Number);
      const rosterComplete = remainingValues.length > 0 && remainingValues.every((n) => n === 0);
      const rosterAlreadyFlagged = rosterCompletedTeamsRef.current.has(team);

      if (rosterComplete && !rosterAlreadyFlagged) {
        rosterCompletedTeamsRef.current.add(team);
        if (!isFirstRun) {
          const rosterSize = Number(stats?.totalPlayers || 0) + remainingValues.reduce((a, b) => a + b, 0);
          newlyCompletedRosters.push({ team, totalPlayers: Number(stats?.totalPlayers || 0), rosterSize });
        }
      } else if (!rosterComplete && rosterAlreadyFlagged) {
        rosterCompletedTeamsRef.current.delete(team);
      }

      if (isFirstRun) return;

      Object.entries(remainingByRole).forEach(([role, remaining]) => {
        const prevRemaining = previous?.[team]?.[role];
        if (Number(prevRemaining) > 0 && Number(remaining) === 0) {
          newlyCompleted.push({ team, role });
        }
      });
    });

    previousRemainingByRoleRef.current = nextSnapshot;

    const teams = Object.keys(nextSnapshot);
    const roleKeys = [...new Set(teams.flatMap((team) => Object.keys(nextSnapshot[team] || {})))];
    const newlyCompletedLeagueRoles = [];

    roleKeys.forEach((role) => {
      const allTeamsDone = teams.length > 0 && teams.every((team) => Number(nextSnapshot[team]?.[role]) === 0);
      const alreadyFlagged = leagueCompletedRolesRef.current.has(role);

      if (allTeamsDone && !alreadyFlagged) {
        leagueCompletedRolesRef.current.add(role);
        if (!isFirstRun) newlyCompletedLeagueRoles.push(role);
      } else if (!allTeamsDone && alreadyFlagged) {
        leagueCompletedRolesRef.current.delete(role);
      }
    });

    if (newlyCompleted.length || newlyCompletedLeagueRoles.length || newlyCompletedRosters.length) {
      setAuctionToasts((prev) => [
        ...prev,
        ...newlyCompleted.map(({ team, role }) => ({
          id: `team-${team}-${role}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          kind: 'team',
          team,
          role,
        })),
        ...newlyCompletedLeagueRoles.map((role) => ({
          id: `league-${role}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          kind: 'league',
          role,
        })),
        ...newlyCompletedRosters.map(({ team, totalPlayers, rosterSize }) => ({
          id: `roster-${team}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          kind: 'roster',
          team,
          totalPlayers,
          rosterSize,
        })),
      ]);
    }
  }, [finalTeamEconomy, auction?.id]);

  // Overlay "Giocatore Chiave preso da un'altra squadra": confronta l'elenco
  // dei giocatori già acquistati con quello del render precedente, e per
  // ogni NUOVO acquisto (non ancora visto) controlla se il giocatore è
  // categorizzato "Chiave" e se non è stato preso dalla propria squadra. Il
  // primo calcolo dopo il mount/cambio asta fa solo da base di partenza.
  useEffect(() => {
    const activePurchases = (purchases || []).filter((p) => !p.deleted);
    const currentIds = new Set(activePurchases.map((p) => String(p.playerId)));
    const previousIds = seenPurchasedPlayerIdsRef.current;
    const isFirstRun = previousIds === null;

    if (!isFirstRun) {
      const myTeamName = auction?.myTeamName;
      const keySnipes = activePurchases.filter((purchase) => {
        if (previousIds.has(String(purchase.playerId))) return false;
        if (myTeamName && purchase.teamName === myTeamName) return false;
        const category = getPlayerCategory(
          { id: purchase.playerId, playerSnapshot: purchase.playerSnapshot },
          playerCategoriesById
        );
        return category === 'key';
      });

      if (keySnipes.length) {
        setAuctionToasts((prev) => [
          ...prev,
          ...keySnipes.map((purchase) => {
            const roleValue = purchase.playerSnapshot?.role ?? purchase.role;
            const roleFamily = getRoleFamily(roleValue, rolesMode);
            const availableKeyCount = availablePlayers.filter(
              (p) =>
                getPlayerCategory(p, playerCategoriesById) === 'key' &&
                getRoleFamily(p.role, rolesMode) === roleFamily
            ).length;

            return {
              id: `key-${purchase.playerId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              kind: 'key-sniped',
              team: purchase.teamName,
              playerName: purchase.playerSnapshot?.name || purchase.playerName || '—',
              role: roleFamily,
              availableKeyCount,
            };
          }),
        ]);
      }
    }

    seenPurchasedPlayerIdsRef.current = currentIds;
  }, [purchases, playerCategoriesById, availablePlayers, auction?.myTeamName, rolesMode]);

  const affordableTeams = useMemo(() => {
    if (!selectedPlayer || !purchasePreview) return [];

    return (teamNames || [])
      .map((team) => {
        const stats = finalTeamEconomy?.[team] || teamEconomy?.[team];
        const maxBidReal = getTeamMaxBidReal(stats, rosterLimits, rolesMode);

        return {
          team,
          maxBidReal,
          canBuy: maxBidReal >= purchasePriceToCheck,
        };
      })
      .filter((item) => item.canBuy)
      .sort((a, b) => b.maxBidReal - a.maxBidReal);
  }, [
    selectedPlayer,
    purchasePreview,
    purchasePriceToCheck,
    teamNames,
    finalTeamEconomy,
    teamEconomy,
    rosterLimits,
    rolesMode,
  ]);

  const boughtPlayersCount = useMemo(
    () => (purchases || []).filter((p) => !p.deleted).length,
    [purchases]
  );

  useEffect(() => {
    if (typeof onAuctionPlayersCountChange === 'function') {
      onAuctionPlayersCountChange(boughtPlayersCount);
    }
  }, [boughtPlayersCount, onAuctionPlayersCountChange]);

  return (
    <section className="auction-market-tab" ref={rootRef}>
      <div className="auction-market-grid">
        <article className="auction-panel glass auction-panel--market-list">
          <div className="panel-head">
            <div>
              <h2>Lista giocatori da acquistare</h2>
              <span>Presenti solamente i giocatori acquistabili</span>
            </div>
          </div>

          <div className="market-filters">
            <label className="market-search">
              <Search size={16} />
              <input ref={searchInputRef} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca giocatore" />
            </label>

            <button
              type="button"
              className="market-filters__mobileToggle"
              onClick={() => setMobileFiltersOpen((v) => !v)}
              aria-expanded={mobileFiltersOpen}
            >
              <span>
                Squadra, categoria e ordinamento
                {(teamFilter !== 'all' || categoryFilter !== 'all' || tierFilter !== 'all' || sortMode !== 'value_desc') && (
                  <span className="market-filters__mobileToggle-badge" />
                )}
              </span>
              {mobileFiltersOpen ? <X size={15} /> : <ChevronDown size={15} />}
            </button>

            <div className={`market-filters__collapsible ${mobileFiltersOpen ? 'is-open' : ''}`}>
            <div className="market-filters__row market-filters__row--extended">
              <select
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
                className="market-sort market-sort--compact"
                aria-label="Filtro squadra"
              >
                {teamOptions.map((team) => (
                  <option key={team} value={team}>
                    {team === 'all' ? 'Tutte le squadre' : team}
                  </option>
                ))}
              </select>

              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="market-sort market-sort--compact"
                aria-label="Filtro categoria"
              >
                {categoryOptions.map((category) => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'Tutte le categorie' : getCategoryLabel(category, customCategoryLabel)}
                  </option>
                ))}
              </select>

              <select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                className="market-sort market-sort--compact"
                aria-label="Filtro fascia"
              >
                {tierOptions.map((tier) => (
                  <option key={tier} value={tier}>
                    {tier === 'all' ? 'Tutte le fasce' : tier === 'none' ? 'Nessuna fascia' : `Fascia ${tier}`}
                  </option>
                ))}
              </select>

              <select value={sortMode} onChange={(e) => setSortMode(e.target.value)} className="market-sort">
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            </div>

            <div className="role-chips role-chips--compact">
              {roleFilterOptions.map((role) => {
                const isActive = role === 'all' ? !roleFilters.length : roleFilters.includes(role);
                return (
                  <button
                    key={role}
                    className={isActive ? 'is-active' : ''}
                    onClick={() => toggleRoleFilter(role)}
                    aria-pressed={isActive}
                    type="button"
                  >
                    {rolesMode === 'classic'
                      ? (CLASSIC_ROLE_LABELS[role] || role)
                      : (role === 'all' ? 'Tutti' : role)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="market-list">
            {filteredPlayers.map((player) => {
              const pmaEntry = marketPmaByPlayerId?.[String(player.id)] || null;
              const category = getPlayerCategory(player, playerCategoriesById);
              const categoryLabel = getCategoryLabel(category, customCategoryLabel);
              const tier = getPlayerTier(player, playerTiersById);
              const sortBadgeSuggestedBid = sortMode === 'pma_delta_desc'
                ? getSmartSuggestedBid({ player, auction, rolesMode, marketContext }).suggestedBid
                : undefined;

              const isNew = !player.previousSeasonStats;

              return (
                <article
                  key={player.id}
                  className={`market-player ${String(player.id) === String(selectedPlayerId) ? 'is-active' : ''}`}
                  onClick={() => setSelectedPlayerId(String(player.id))}
                >
                  {category !== 'none' && (
                    <Tooltip
                      as="span"
                      className={`market-player__catBar market-player__catBar--${category}`}
                      content={categoryLabel}
                      aria-label={`Categoria: ${categoryLabel}`}
                    />
                  )}

                  <div className="market-player__left">
                    <div className="market-player__badgeCol">
                      <TeamBadge team={player.team} size={30} />

                      <div className="market-player__roles">
                        {rolesMode === 'mantra'
                          ? getRoleTokens(player.role, rolesMode).map((r, idx) => (
                            <span
                              key={`${player.id}-${r}-${idx}`}
                              className={`auction-role-chip auction-role-chip--mantra auction-role-chip--mantra-${getMantraRoleGroup(r)}`}
                            >
                              {r}
                            </span>
                          ))
                          : <RoleChip role={player.role} />}
                      </div>
                    </div>

                    <div className="market-player__body">
                      <div className="market-player__nameLine">
                        <strong>{player.name}</strong>
                        {isNew && (
                          <Tooltip as="span" className="market-player__newTag" content="Giocatore nuovo">
                            NUOVO
                          </Tooltip>
                        )}
                        {category !== 'none' && (
                          <span className={`market-player__categoryTag market-player__categoryTag--${category}`}>
                            {categoryLabel}
                          </span>
                        )}
                        {tier > 0 && (
                          <span className={`market-player__tierTag market-player__tierTag--${tier}`}>
                            Fascia {tier}
                          </span>
                        )}
                      </div>
                      <span>{player.team}</span>

                      <div className="market-player__statLine">
                        Val <strong>{fmt(player.value)}</strong>
                        <span className="market-player__statSep" aria-hidden="true">·</span>
                        MV <strong>{fmt(player.markavg)}</strong>
                        <span className="market-player__statSep" aria-hidden="true">·</span>
                        FV <strong>{fmt(player.fmarkavg)}</strong>
                        <Tooltip
                          as="span"
                          className="market-player__statInline market-player__statInline--pma"
                          content="Prezzo Medio d'Acquisto nelle altre aste"
                        >
                          <span className="market-player__statSep" aria-hidden="true">·</span> PMA{' '}
                          <strong>{pmaEntry?.average !== null && pmaEntry?.average !== undefined ? fmt(pmaEntry.average) : '—'}</strong>
                        </Tooltip>

                        {SORT_BADGE[sortMode] && (
                          <Tooltip
                            as="span"
                            className="market-player__statInline market-player__statInline--sort"
                            content={`Ordinamento attivo: ${SORT_OPTIONS.find(o => o.value === sortMode)?.label}`}
                          >
                            <span className="market-player__statSep" aria-hidden="true">·</span>{' '}
                            {SORT_BADGE[sortMode].label} <strong>{SORT_BADGE[sortMode].getValue(player, pmaEntry, sortBadgeSuggestedBid, playProbabilityById?.[String(player.id)])}</strong>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="market-player__right">
                    <Tooltip
                      as="button"
                      className="market-player__detail"
                      content="Dettaglio giocatore"
                      aria-label="Dettaglio giocatore"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDetailFromPlayer(player.id);
                      }}
                      type="button"
                    >
                      <ExternalLink size={14} />
                    </Tooltip>
                  </div>
                </article>
              );
            })}
          </div>
        </article>

        <aside className="auction-panel glass auction-purchase-panel">
          <div className="selected-player-panel">
            <div className="panel-head">
              <div>
                <h2>Giocatore selezionato</h2>
                <span>Giocatore attualmente in fase d'asta</span>
              </div>
            </div>

            {selectedPlayer ? (
              <>
                <div className="selected-player-card">
                  <div className="selected-player-card__head">
                    <TeamBadge team={selectedPlayer.team} size={52} />
                    <div className="selected-player-card__identity">
                      <div className="selected-player-card__identityTop">
                        <strong>{selectedPlayer.name}</strong>

                        <div className="selected-player-card__roleWrap">
                          {!selectedPlayer.previousSeasonStats && (
                            <Tooltip as="span" className="market-player__new-chip" content="Giocatore senza statistiche stagione precedente">
                              Nuovo
                            </Tooltip>
                          )}
                          {renderFullRoleBadges(selectedPlayer.role, rolesMode)}
                        </div>
                      </div>
                      <div className="selected-player-card__teamRow">
                        <span>{selectedPlayer.team}</span>
                        <CategoryChip
                          category={selectedCategory}
                          title={`Categoria giocatore: ${getCategoryLabel(selectedCategory, customCategoryLabel)}`}
                          className="selected-player-card__categoryChip"
                          customLabel={customCategoryLabel}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="selected-player-card__statsTable">
                    <div className="selected-player-card__statCell">
                      <span>Presenze</span>
                      <strong>{selectedPlayer.caps || 0}</strong>
                    </div>
                    <div className="selected-player-card__statCell">
                      <span>Media voto</span>
                      <strong>{fmt(selectedPlayer.markavg)}</strong>
                    </div>

                    {getRoleFamily(selectedPlayer.role, rolesMode) === 'P' ? (
                      <>
                        <div className="selected-player-card__statCell">
                          <span>Gol subiti</span>
                          <strong>{selectedPlayer.gotgoals ?? selectedPlayer.goalsConceded ?? selectedPlayer.goals_against ?? 0}</strong>
                        </div>
                        <div className="selected-player-card__statCell">
                          <span>Media fanta voto</span>
                          <strong>{fmt(selectedPlayer.fmarkavg)}</strong>
                        </div>
                        <div className="selected-player-card__statCell">
                          <span>Rigori parati</span>
                          <strong>
                            {selectedPlayer.savedPenalties ??
                              selectedPlayer.penaltiesSaved ??
                              selectedPlayer.penalties_saved ??
                              selectedPlayer.saves ??
                              0}
                          </strong>
                        </div>
                        <div className="selected-player-card__statCell">
                          <span>Valutazione</span>
                          <strong>{fmt(selectedPlayer.value)}</strong>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="selected-player-card__statCell">
                          <span>Gol fatti</span>
                          <strong>{selectedPlayer.goals || 0}</strong>
                        </div>
                        <div className="selected-player-card__statCell">
                          <span>Media fanta voto</span>
                          <strong>{fmt(selectedPlayer.fmarkavg)}</strong>
                        </div>
                        <div className="selected-player-card__statCell">
                          <span>Assist</span>
                          <strong>{selectedPlayer.assists || 0}</strong>
                        </div>
                        <div className="selected-player-card__statCell">
                          <span>Valutazione</span>
                          <strong>{fmt(selectedPlayer.value)}</strong>
                        </div>
                      </>
                    )}

                    <div className="selected-player-card__statCell selected-player-card__statCell--full">
                      <span>Impiego</span>
                      <strong>{getCompactPlayProbabilityLabel(playProbabilityById?.[String(selectedPlayer.id)])}</strong>
                    </div>
                    {hasSelectedGoalkeeperBackup && (
                      <div className="selected-player-card__statCell selected-player-card__statCell--full">
                        <span>Riserva</span>
                        <strong>{selectedGoalkeeperBackup}</strong>
                      </div>
                    )}
                  </div>

                  <div className="selected-player-card__fixtures">
                    <span className="selected-player-card__fixtures-label">Prossime partite</span>
                    <TeamNextFixtures team={selectedPlayer.team} count={5} />
                  </div>

                  {selectedPlayerNote ? (
                    <Tooltip as="div" className="selected-player-card__notes" content={selectedPlayerNote}>
                      <span>Note</span>
                      <p>{selectedPlayerNotePreview}</p>
                    </Tooltip>
                  ) : null}

                  {selectedPurchase && <div className="already-owned">Acquistato da {selectedPurchase.teamName}</div>}
                </div>

                <div className="purchase-form">
                  <div className={`purchase-form__buyRow ${teamNames.length === 1 ? 'purchase-form__buyRow--single-team' : ''}`}>
                    <label>
                      <span>Crediti d'acquisto</span>
                      <input
                        ref={creditsInputRef}
                        type="number"
                        min="1"
                        value={spentCredits}
                        onChange={(e) => setSpentCredits(e.target.value)}
                        placeholder="Es. 24"
                      />
                    </label>

                    {!readOnly && <button
                      className="confirm-btn confirm-btn--inline"
                      onClick={handlePurchase}
                      disabled={busy || !selectedPlayer || !spentCredits || !winningTeam}
                      type="button"
                    >
                      {busy ? (
                        <>
                          <Loader2 size={16} className="spin" />
                          Salvataggio...
                        </>
                      ) : selectedPurchase ? (
                        <>
                          <CheckCircle2 size={16} />
                          Aggiorna acquisto
                        </>
                      ) : (
                        <>
                          <PlusCircle size={16} />
                          Conferma acquisto
                        </>
                      )}
                    </button>}

                    {!readOnly && teamNames.length === 1 ? (
                      <Tooltip
                        as="button"
                        className="confirm-btn confirm-btn--secondary"
                        type="button"
                        onClick={handlePurchaseAsOtherTeam}
                        disabled={busy || !selectedPlayer}
                        content="Rimuovi il giocatore dalla lista"
                      >
                        <PlusCircle size={16} />
                        Rimuovi
                      </Tooltip>
                    ) : null}
                  </div>

                  <label>
                    <span>Squadra vincente</span>
                    <select value={winningTeam} onChange={(e) => setWinningTeam(e.target.value)}>
                      <option value="">Seleziona squadra</option>
                      {teamNames.map((team) => (
                        <option key={team} value={team}>
                          {team}
                        </option>
                      ))}
                    </select>
                  </label>

                  {purchasePreview ? (
                    <div className="purchase-hints purchase-hints--smart">
                      <div className="purchase-hints__grid">
                        <article className="purchase-hint-card purchase-hint-card--bid">
                          <span>Puntata attuale</span>
                          <strong>{purchasePreview.enteredCredits || '—'} cr</strong>
                          <small>
                            {Math.round(purchasePreview.percentOfInitialBudget || 0)}% del budget iniziale ·{' '}
                            {Math.round(purchasePreview.percentOfTeamBudget || 0)}% del residuo squadra
                          </small>

                          <div className="purchase-hint-meter">
                            <i
                              style={{
                                width: `${Math.min(Math.max(purchasePreview.percentOfInitialBudget || 0, 0), 100)}%`,
                              }}
                            />
                          </div>
                        </article>

                        <article className={`purchase-hint-card ${purchasePreview.canBuy ? 'is-ok' : 'is-warning'}`}>
                          <span>Crediti dopo l’acquisto</span>
                          <strong>{purchasePreview.remainingAfterPurchase} cr</strong>
                          <small>
                            {purchasePreview.canBuy
                              ? `${purchasePreview.reserveSlotsAfterPurchase} slot ancora da coprire`
                              : purchasePreview.status}
                          </small>
                        </article>

                        <article className={`purchase-hint-card ${purchasePreview.canBuy ? 'is-ok' : 'is-warning'}`}>
                          <span>Puntata massima</span>
                          <strong>{purchasePreview.maxBid > 0 ? `${purchasePreview.maxBid} cr` : 'Non acquistabile'}</strong>
                          <small>Va lasciato almeno 1 credito per ogni slot rimanente.</small>
                        </article>

                        <article
                          className={`purchase-hint-card purchase-hint-card--suggested purchase-hint-card--${purchasePreview.suggestedBand}`}
                        >
                          <span>
                            Prezzo per{' '}
                            <em>
                              {selectedPlayer?.name || selectedPlayer?.playerSnapshot?.name || 'questo giocatore'}
                            </em>
                          </span>

                          <div className="purchase-hint-card__priceRow">
                            <Tooltip
                              className="purchase-hint-card__priceItem"
                              title="Prezzo suggerito"
                              description="In base al ruolo, alla valutazione e al budget di questa asta."
                            >
                              <span className="purchase-hint-card__priceValue">
                                {purchasePreview.suggestedBid > 0 ? (
                                  <>{purchasePreview.suggestedBid}<span className="purchase-hint-card__priceUnit"> cr</span></>
                                ) : '—'}
                              </span>
                              <span className="purchase-hint-card__priceLabel">suggerito</span>
                            </Tooltip>

                            {purchasePreview.reactiveBid > 0 && (
                              <>
                                <span className="purchase-hint-card__priceSep" aria-hidden="true">/</span>
                                <Tooltip
                                  className="purchase-hint-card__priceItem"
                                  title="Prezzo reattivo"
                                  description={
                                    purchasePreview.reactiveSampleSize > 0
                                      ? `Corretto sui crediti residui reali delle ${purchasePreview.reactiveSampleSize} squadre che possono ancora acquistare questo giocatore.`
                                      : 'Nessuna squadra ha più slot liberi per questo ruolo: mostrato il prezzo suggerito.'
                                  }
                                >
                                  <span className="purchase-hint-card__priceValue purchase-hint-card__priceValue--reactive">
                                    {purchasePreview.reactiveBid}<span className="purchase-hint-card__priceUnit"> cr</span>
                                  </span>
                                  <span className="purchase-hint-card__priceLabel purchase-hint-card__priceLabel--reactive">reattivo</span>
                                </Tooltip>
                              </>
                            )}

                            {Number(playerMaxSpendById?.[String(selectedPlayer?.id)] || 0) > 0 && (
                              <>
                                <span className="purchase-hint-card__priceSep" aria-hidden="true">/</span>
                                <Tooltip
                                  className="purchase-hint-card__priceItem"
                                  title="Il tuo tetto"
                                  description="Impostato in Svincolati o importato da una strategia."
                                >
                                  <span className="purchase-hint-card__priceValue">
                                    {playerMaxSpendById[String(selectedPlayer.id)]}<span className="purchase-hint-card__priceUnit"> cr</span>
                                  </span>
                                  <span className="purchase-hint-card__priceLabel">tetto</span>
                                </Tooltip>
                              </>
                            )}
                          </div>

                          <small>{purchasePreview.suggestedNote}</small>
                          {purchasePreview.suggestedOverMax && (
                            <small className="purchase-hint-card__overmax">
                              Sfora la puntata massima di {purchasePreview.suggestedDeltaOverMax} cr: è una stima di
                              valore, non un limite.
                            </small>
                          )}
                        </article>

                        <article className="purchase-hint-card purchase-hint-card--teams purchase-hint-card--teams-full">
                          <span>
                            Squadre che possono acquistare{' '}
                            <em>
                              {selectedPlayer?.name || selectedPlayer?.playerSnapshot?.name || 'questo giocatore'}
                            </em>
                            {affordableTeams.length > 0 && (
                              <strong className="purchase-hint-card__teamsCount"> ({affordableTeams.length})</strong>
                            )}
                          </span>

                          <div className="purchase-hint-card__teams">
                            {affordableTeams.length ? (
                              affordableTeams.map((item) => (
                                <div key={item.team} className="purchase-hint-team">
                                  <TeamBadge team={item.team} size={16} />
                                  <strong>{item.team}</strong>
                                  <span>{fmt(item.maxBidReal)} cr max</span>
                                </div>
                              ))
                            ) : (
                              <small>Nessuna squadra può acquistarlo.</small>
                            )}
                          </div>
                        </article>
                      </div>
                    </div>
                  ) : (
                    <div className="purchase-hints purchase-hints--smart">
                      <div className="purchase-hints__empty">
                        Seleziona squadra vincente e crediti spesa per vedere i calcoli.
                      </div>
                    </div>
                  )}

                  {purchaseError && <div className="purchase-error">{purchaseError}</div>}

                  <div className="purchase-actions">
                    {!readOnly && selectedPurchase && (
                      <button
                        className="delete-btn"
                        type="button"
                        onClick={() => onRemovePurchase(selectedPlayer.id)}
                        disabled={busy}
                      >
                        <Trash2 size={16} />
                        Rimuovi
                      </button>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="empty-box">Seleziona un giocatore per continuare.</div>
            )}
          </div>
        </aside>

        <article className="auction-panel glass auction-panel--market-teams team-summary-full">
          <div className="panel-head">
            <div>
              <h2>Riepilogo squadre</h2>
              <span>Squadre, crediti residui e slot ancora da coprire.</span>
            </div>
          </div>

          <div className="team-summary-list">
            {[...teamNames]
              .sort((a, b) => {
                const aCredits = finalTeamEconomy?.[a]?.remaining ?? auction?.credits ?? 0;
                const bCredits = finalTeamEconomy?.[b]?.remaining ?? auction?.credits ?? 0;
                return bCredits - aCredits;
              })
              .map((team) => {
                const stats = finalTeamEconomy[team] || {
                  remaining: auction?.credits || 0,
                  spent: 0,
                  totalPlayers: 0,
                  spentByRole: {},
                  purchasedByRole: {},
                  remainingByRole: rosterLimits,
                  remainingMinByRole: rosterMinLimits || rosterLimits,
                };

                return (
                  <TeamSummaryRow
                    key={team}
                    team={team}
                    stats={stats}
                    rosterLimits={rosterLimits}
                    isMine={team === auction?.myTeamName}
                    rolesMode={rolesMode}
                    readOnly={readOnly}
                    onOpen={() => {
                      setActiveTeamOverlay(team);
                      if (typeof onOpenTeamOverlay === 'function') onOpenTeamOverlay(team);
                    }}
                  />
                );
              })}
          </div>
        </article>
      </div>

      <section className="auction-bottom-grid">
        <article className="auction-panel glass purchase-history-full">
          <div className="panel-head">
            <div>
              <h2>Ultimi acquisti</h2>
              <span>{purchases.filter((p) => !p.deleted).length} giocatori acquistati</span>
            </div>
          </div>

          <div className="purchase-list purchase-list--wide">
            {purchases
              .filter((p) => !p.deleted)
              .slice(0, 6)
              .map((purchase) => (
                <PurchaseRow
                  key={purchase.id}
                  purchase={purchase}
                  rolesMode={rolesMode}
                  readOnly={readOnly}
                  onEdit={() => setEditingPurchase(purchase)}
                  onRemove={() => onRemovePurchase(purchase.playerId)}
                  onOpenDetail={() => openDetailFromPlayer(purchase.playerId)}
                />
              ))}
          </div>
        </article>
      </section>

      {purchaseOutcome && <PurchaseOutcomeOverlay outcome={purchaseOutcome} onClose={() => setPurchaseOutcome(null)} />}

      <AuctionToastStack
        toasts={auctionToasts}
        onDismiss={(id) => setAuctionToasts((prev) => prev.filter((t) => t.id !== id))}
      />

      {activeTeamOverlay && (
        <TeamRosterOverlay
          teamName={activeTeamOverlay}
          purchases={(purchases || []).filter((p) => !p.deleted && p.teamName === activeTeamOverlay)}
          stats={finalTeamEconomy[activeTeamOverlay]}
          rosterLimits={rosterLimits}
          credits={auction.credits}
          rolesMode={rolesMode}
          onClose={() => setActiveTeamOverlay(null)}
          onOpenDetail={openDetailFromPlayer}
          onEditPurchase={setEditingPurchase}
        />
      )}

      {editingPurchase && (
        <PurchaseEditOverlay
          purchase={editingPurchase}
          teamNames={teamNames}
          rolesMode={rolesMode}
          onClose={() => setEditingPurchase(null)}
          onSave={handleSaveEditedPurchase}
          onDelete={async (playerId) => {
            setBusy(true);
            try {
              await onRemovePurchase(playerId);
              setEditingPurchase(null);
            } finally {
              setBusy(false);
            }
          }}
          loading={busy}
        />
      )}
    </section>
  );
}